import { api } from '@/lib/api';
import { pageTitle } from '@/lib/page-title';
import { useAuth } from '@/contexts/useAuth';
import { ShareCard } from '@/features/reel/ShareCard';
import { useCallback, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type CSSProperties, type DragEvent, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AudioWaveform, Brush, Check, Eraser, LogIn, ChevronDown, SunMedium, ChevronLeft, ChevronRight, Columns2, Contrast, Crop, Download, Film, Image as ImageIcon, ImagePlus, Layers, Loader2, type LucideIcon, Mountain, Move, Music, Pause, Play, Plus, QrCode, Redo2, Replace, RotateCcw, ScanSearch, Scissors, Share2, Shirt, SlidersHorizontal, Smartphone, Sparkles, Stamp, Sun, Undo2, Upload, Wind, X } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { ApprovalRail } from '@/components/trust/ApprovalRail';
import { SystemChecks } from '@/components/trust/SystemChecks';
import { CheckoutStrip } from '@/components/trust/CheckoutStrip';
import { SupportLine } from '@/components/trust/SupportLine';
import { DEFAULT_CENTS, QuickToolCards } from '@/components/quicktools/QuickToolCards';
import { StudioList } from '@/components/layout/StudioList';
import quickTools from '@/components/quicktools/tools.json';
import { addCoverExample, downloadUrl, getCoverExamples, getDepthPreview, getFramePreview, getJob, getMotionPreview, getQuickToolsConfig, isExamplesAdmin, money, previewUrl, readOperatorKey, rememberQuickJob, remakeWithoutMark, startCheckout, uploadJob, type DepthPreview, type FramePreview, type QuickJob, type QuickToolConfig, type QuickToolKey, payWithCredits } from '@/lib/quicktools-api';
import { BackdropStack, LivingCover } from '@/components/quicktools/LivingCover';
import { OverlayPlacer } from '@/components/quicktools/OverlayPlacer';
import { placeIsNone, placeTurn, type Place } from '@/lib/float-scene.mjs';
import { BehindStep, useLiveCut, CUT_BRUSH_R, CUT_SIZES, CutLayer, CutSizes, MoveStep, PaintLayer, REPLACE_IDS, ZoomBox, decodeStrokes, distanceOf, strokeAdds, type BehindMode, type CutSize, type PaintState } from '@/components/quicktools/MotionSteps';
import { CutEdit } from '@/lib/cut-edit';
import { songBassLevel } from '@/lib/bass-level';
import { frameMap } from '@/lib/paint-motion';
import { CoverShapes, type CoverPurpose } from '@/components/quicktools/CoverShapes';
import { cropStill, formatPlace, parsePlace, platformCount, ratioLabel, shapePlan, sizeOf, type ShapeKey } from '@/lib/cover-shapes';
import { LibraryPicker, type PickItem } from '@/components/quicktools/LibraryPicker';
import { SongPartPicker } from '@/components/quicktools/SongPartPicker';
import { LyricsOverlay, ReleaseCard, ReleasePicker, previewLyricLines, releaseWords, type ReleaseKind, type TextLang } from '@/components/quicktools/ReleaseVersions';
import LIBRARY from '@/lib/overlay-library.json';
import EDITOR_PAGES from '@/components/editorPages/pages.json';
import { CoverExamples, ExampleReel, useCoverExamples } from '@/components/quicktools/CoverExamples';
import { PhonePair } from '@/components/quicktools/PhoneCompare';
import { CanvasAppsLine, CanvasByline } from '@/components/quicktools/CanvasLines';
import { trackEvent, trackPageView } from '@/lib/analytics';
import { ShareToolBar } from '@/components/share/ShareToolBar';
import { cn } from '@/lib/utils';

// A QUICK TOOL'S PAGE (2026-10-16): /captions, /faceless, /hooks, /canvas and
// /vows, inside the site's frame like every other tool (owner: "they need to
// be presented correctly as is every other tool we have"). The first screen
// follows /editor's (Studio Blueprint 01): the promise, check chips, ONE big
// upload button and a real sample made by this tool. Then the free preview,
// and the one paid button with its price on it and the checkout strip under
// it. The work happens on the server's CPU (routes/quicktools.js): no
// account, no credits, no GPU.
//
// /<tool>/<job id> is the same page showing one job; Stripe returns there
// with ?session_id= and the download unlocks. A seller's phone opened once
// with ?op=<key> sells in person: $10/$15/$20 chips and a pay QR.

type ToolSpec = (typeof quickTools.tools)[keyof typeof quickTools.tools];
interface FieldSpec {
  name: string;
  type: string;
  rows?: number;
  max?: number;
  required?: boolean;
  checked?: boolean;
  options?: string[];
  /**
   * Only for this kind of upload (owner 2026-10-16: no video start time for a
   * picture); 'song' once a song is picked; 'mark' while the site mark is in place.
   */
  when?: 'video' | 'image' | 'song' | 'mark';
  /** A moving example of what this switch does (the Spotify loop's effects). */
  example?: string;
  /** A 'choice' field: the option picked at first, and a moving example per option. */
  default?: string;
  examples?: Record<string, string>;
  /** A 'multi' field (several at once, up to picks, sent as a comma list): each option shown live (the floating things). */
  picks?: number;
  live?: boolean;
}
interface UploadSpec {
  field: string;
  accept: string;
  optional?: boolean;
  multiple?: number;
}

type Stage = 'upload' | 'pay' | 'work' | 'ready';
/** One stroke on the crop step: its number, its text as drawn, and a line's own numbers to lay it on again (Redo). */
type CutMark = { id: number; text: string; line?: { pts: [number, number][]; erase: boolean; r: number } };
/** What a check answered for the strokes `ids` ("3,5,"), in one placement of the picture. */
type CutAnswer = { ctx: string; ids: string; mask: string | null };
type CutSave = Record<'keep' | 'replace', { edit: ReturnType<CutEdit['snapshot']> | null; marks: CutMark[] }>;
/** One step Undo takes back: every choice as it was, and the stroke or the whole cut when that is what changed. */
type HistStep = { choices: Record<string, string>; stroke?: { mode: 'keep' | 'replace'; mark: CutMark }; cut?: CutSave };
// The Living Cover's library (overlay-library.json, scripts/overlays/make-loops.cjs): the owner's picked clips.
type LibItem = { group: string; kind: string; en: string; es: string; file: string; preview: string; thumb?: string; blend?: string };
const LIB_OVERLAYS = LIBRARY.overlays as Record<string, LibItem>;
const LIB_BACKDROPS = LIBRARY.backdrops as Record<string, LibItem>;
// The engine's own kinds (float-scene.mjs), placed in the library's groups with a still each (library-sort.cjs).
const LIB_DRAWN = ((LIBRARY as { drawn?: Record<string, { group: string; thumb: string; blend?: string }> }).drawn || {});
const libName = (item: { en: string; es: string }, lang: string) => (lang.startsWith('es') ? item.es : item.en);
const IN_PERSON = [1000, 1500, 2000];

// The Living Cover's extra files, in the order they are offered (keys of the job's extra_files).
const EXTRA_DOWNLOADS = ['whatsapp', 'whole', 'youtube', 'song_15s', 'song_60s', '9x16', 'square', '4x5', '3x4', '16x9', 'orig', 'apple_1x1', 'apple_3x4', 'soundcloud'];

export default function QuickToolPage({ tool }: { tool: QuickToolKey }) {
  const { t, i18n } = useTranslation();
  const spec = quickTools.tools[tool] as ToolSpec & { formTop?: boolean };
  const { id: routeId } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = new URLSearchParams(location.search).get('session_id');
  const k = (key: string, opts?: Record<string, unknown>) => t(`qt.tools.${tool}.${key}`, opts);

  const [config, setConfig] = useState<QuickToolConfig | null>(null);
  const [stage, setStage] = useState<Stage>(routeId ? 'work' : 'upload');
  const [job, setJob] = useState<QuickJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [picked, setPicked] = useState<Record<string, File[]>>({});
  const [choices, setChoices] = useState<Record<string, string>>({});
  // The main upload decides video or picture (a song picked first must not hide the picture effects).
  const mainField = (spec.uploads as UploadSpec[]).find((u) => !u.optional)?.field;
  // (Only the main upload: the song can be picked first, on the same screen, owner 2026-10-23.)
  const firstPicked = mainField ? picked[mainField]?.[0] : Object.values(picked).flat()[0];
  // Any cover can carry a song, a personal one too (owner 2026-10-24).
  const hasSong = !!picked.song?.length;
  // The words on the videos: the buyer's pick, else the language the page is in.
  const textLang: TextLang = (choices.textlang as TextLang) || (i18n.language.startsWith('es') ? 'es' : 'en');
  // The live preview's lyrics follow the song while it plays (seconds into the part), and the song's length.
  const [lyricTime, setLyricTime] = useState<number | null>(null);
  const [songSeconds, setSongSeconds] = useState(0);
  // The release version shown big on the picture (a tap on its preview in the Export step).
  const [releaseShown, setReleaseShown] = useState<ReleaseKind | null>(null);
  // A new song starts from its own loudest part (the picker sets it once it has read the song).
  const songFile = picked.song?.[0];
  useEffect(() => {
    setChoices((prev) => (prev.songstart ? { ...prev, songstart: '' } : prev));
  }, [songFile]);
  const pickedKind: 'video' | 'image' | null = !firstPicked ? null : firstPicked.type.startsWith('video') ? 'video' : firstPicked.type.startsWith('image') ? 'image' : null;
  // The picked picture, for the Living Cover's live preview.
  const pictureUrl = useMemo(() => (firstPicked && pickedKind === 'image' ? URL.createObjectURL(firstPicked) : null), [firstPicked, pickedKind]);
  // The Living Cover's depth in the live preview: the server cuts the picked picture out with the cover's own engine,
  // and again when the customer says what the picture is of (a scene has none). Flat until it answers.
  const [depth, setDepth] = useState<DepthPreview | null>(null);
  // While the server is cutting the picture out (the backdrop tab says so).
  const [depthBusy, setDepthBusy] = useState(false);
  // The check could not run (server busy, too many tries) and there is no earlier answer for this picture: the Crop
  // step says so with a button to check again, never "we couldn't find a sky" (owner 2026-10-26: a sitting person over a
  // city skyline, "by the time i got to the step 3 cropping option it would not allow me to choose anything").
  const [depthFailed, setDepthFailed] = useState(false);
  const [depthRetry, setDepthRetry] = useState(0);
  // One question (owner 2026-10-21: "is the image someone ... if not it will be treated as a inanimate object"): the field's default until answered.
  // (2026-10-23, the 7-step path: the picture check says what it found, 'auto' asked, and the buyer taps only if it is wrong.)
  const askSubject = choices.subject ?? 'auto';
  const subjectSaid = choices.subject ?? depth?.suggest ?? (spec.fields as FieldSpec[]).find((f) => f.name === 'subject')?.default ?? '';
  // The customer removed their photo's background (their own step, owner 2026-10-21), or uploaded one already cut out.
  const cutOn = subjectSaid !== 'scene' && (choices.cutout === 'on' || depth?.why === 'cut out already');
  const backdropOn = cutOn && choices.behind === 'background' && Boolean(depth?.depth) && backdropIds(choices.backdrop).length > 0;
  // What the backdrops send besides the list: each one's see-through slider and place (owner 2026-10-27).
  const backdropSent = backdropOn ? { backdrop: backdropIds(choices.backdrop).join(','), bopacity: choices.bopacity ?? '', bspot: choices.bspot ?? '' } : { backdrop: '', bopacity: '', bspot: '' };
  // true: remove the background; 'no': the customer said no, keep it (owner 2026-10-21: "there is no way to say no").
  const setCut = (on: boolean | 'no') => setChoices((prev) => ({ ...prev, cutout: on === true ? 'on' : on === 'no' ? 'no' : '', ...(on === true ? {} : { backdrop: '' }) }));
  // WHERE WILL YOU POST IT (owner 2026-10-23): the places picked, the shapes they need (cover-shapes.ts, the server's
  // own rules), and where the picture sits in each. "Just for me" with shapes picked freely sends those shapes alone.
  // Two kinds of buyer (owner 2026-10-23, "split it that way"): an artist promoting a song (the platforms), or someone
  // who just wants a moving picture (their picture's own shape, or shapes picked freely; no song).
  const purpose = (tool === 'canvas' && (choices.purpose === 'artist' || choices.purpose === 'picture') ? choices.purpose : '') as CoverPurpose | '';
  const pictureOnly = purpose === 'picture';
  const tierPicked = hasSong ? (choices.length === 'full' || choices.length === 'pack' ? choices.length : 'clip') : null;
  const platformsPicked = useMemo(() => (choices.platforms ?? '').split(',').filter((x) => x && x !== 'me'), [choices.platforms]);
  const freePicked = useMemo(() => (choices.shapes ?? '').split(',').filter(Boolean), [choices.shapes]);
  const sentPlatforms = pictureOnly ? (freePicked.length ? [] : ['me']) : platformsPicked;
  const sentFree = pictureOnly ? freePicked : [];
  const coverPlanRaw = useMemo(() => shapePlan(sentPlatforms, sentFree, tierPicked, pictureOnly), [sentPlatforms.join(','), sentFree.join(','), tierPicked, pictureOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  const places = useMemo(() => parsePlace(choices.place ?? ''), [choices.place]);
  const setPlace = (key: ShapeKey, place: { mode: 'fill' | 'crop'; pos: number } | null) =>
    setChoices((prev) => {
      const now = parsePlace(prev.place ?? '');
      if (place) now[key] = place;
      else delete now[key];
      return { ...prev, place: formatPlace(now) };
    });
  // The picture's own size (its shape, and "its own shape" for Just for me).
  const [picDims, setPicDims] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    setPicDims(null);
    if (!pictureUrl) return undefined;
    const img = new Image();
    let live = true;
    img.onload = () => { if (live) setPicDims({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.src = pictureUrl;
    return () => { live = false; };
  }, [pictureUrl]);
  // The picture's own shape when it is one of the others picked is made once (canvas.js does the same).
  const coverPlan = useMemo(() => {
    if (!coverPlanRaw.shapes.includes('orig') || !picDims) return coverPlanRaw;
    const o = sizeOf('orig', picDims);
    const same = coverPlanRaw.shapes.find((k) => k !== 'orig' && sizeOf(k, picDims).w === o.w && sizeOf(k, picDims).h === o.h);
    if (!same) return coverPlanRaw;
    const main = coverPlanRaw.main === 'orig' ? same : coverPlanRaw.main;
    return { ...coverPlanRaw, main, shapes: [main, ...coverPlanRaw.shapes.filter((k) => k !== 'orig' && k !== main)] };
  }, [coverPlanRaw, picDims]);
  // Each shape's picture from the server (filled around, never cut), asked again only for a shape whose placement changed.
  const [framePrev, setFramePrev] = useState<(FramePreview & { keys: Record<string, string> }) | null>(null);
  const [frameBusy, setFrameBusy] = useState(false);
  useEffect(() => setFramePrev(null), [firstPicked]);
  // Where the buyer tapped the subject the picture check missed (owner 2026-10-23: "it couldnt recognize the little
  // person sitting on the ledge"): every check of the picture looks there.
  const tapAt = askSubject === 'scene' ? '' : choices.tap ?? '';
  // The part painted to be replaced by a new sky or scene (owner 2026-10-27: "i wanted to crop out the sky and replace
  // it"): every check of the picture finds that as the sky.
  const replaceAt = choices.replace ?? '';
  // The buyer chose to remove the background (owner 2026-10-27: "why isnt there a auto crop feature that gets rid of ...
  // the entire background"): the check cuts out the model's best subject at once, like ChopBox's background removal.
  const removeBg = choices.behind === 'background';
  // THE BRUSH ON THE CROP STEP (owner 2026-10-27): what it marks ('keep': what stays in front of a new background;
  // 'replace': the part a new sky or scene goes in), and whether it rubs out ("maybe a eraser is a good addition").
  const [brush, setBrush] = useState<{ mode: 'keep' | 'replace' | null; erase: boolean }>({ mode: null, erase: false });
  const tapping = brush.mode !== null;
  // Strokes painted since the brush was picked up: sent together on Done (one picture check for all of them).
  const [paintDraft, setPaintDraft] = useState('');
  // What the Crop step shows (owner 2026-10-27: painting "while the image moves and has a bright ass glow making it hard
  // to see the borders of anything"): 'select', the picture still and plain with what is selected tinted; 'result', the
  // living preview with the new sky or background.
  const [cropView, setCropView] = useState<'select' | 'result'>('select');
  // THE BRUSH PICTURES (owner 2026-10-28: "we shouldnt need to put a limit on that"): every line brushed goes into one
  // picture of the photo per choice (cut-edit.ts), sent as a PNG; the taps stay points ('#n' their number in the order).
  const cutEdits = useRef<{ keep: CutEdit | null; replace: CutEdit | null }>({ keep: null, replace: null });
  const cutSeq = useRef({ keep: 0, replace: 0 });
  // Each picture's version (a line or an undo bumps it), and the version the checks were last asked with.
  const [editVer, setEditVer] = useState({ keep: 0, replace: 0 });
  const editVerRef = useRef(editVer);
  editVerRef.current = editVer;
  const [editSent, setEditSent] = useState({ keep: 0, replace: 0 });
  // EVERY STROKE STILL STANDING, in order, and what each check answered for a run of them (owner 2026-11-01: "the button
  // to undo your last action doesnt work 75 percent of the time": a stroke the check had already answered stayed on the
  // picture until the next check came back). The picture shown is the longest answer those strokes start with, the
  // strokes after it laid on by the page (CutLayer): taking one back shows at once, with no check to wait for.
  const cutMarks = useRef<Record<'keep' | 'replace', CutMark[]>>({ keep: [], replace: [] });
  const cutAnswers = useRef<Record<'keep' | 'replace', CutAnswer[]>>({ keep: [], replace: [] });
  const marksOf = (mode: 'keep' | 'replace') => cutMarks.current[mode].map((m) => `${m.id},`).join('');
  // The strokes the next check asks with: what the last commit sent.
  const sentMarks = useRef({ keep: '', replace: '' });
  useEffect(() => {
    cutEdits.current = { keep: null, replace: null };
    cutSeq.current = { keep: 0, replace: 0 };
    cutMarks.current = { keep: [], replace: [] };
    cutAnswers.current = { keep: [], replace: [] };
    sentMarks.current = { keep: '', replace: '' };
    hist.current = { ...hist.current, past: [], future: [], at: 0, keys: '', cut: null };
    setHistTick((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPicked]);
  const editOf = (mode: 'keep' | 'replace') => {
    if (!cutEdits.current[mode] && picDims) cutEdits.current[mode] = new CutEdit(picDims.w, picDims.h);
    return cutEdits.current[mode];
  };
  const editBlobs = async () => ({
    tap: (await cutEdits.current.keep?.blob()) ?? null,
    replace: (await cutEdits.current.replace?.blob()) ?? null,
  });
  const clearEdit = (mode: 'keep' | 'replace') => {
    cutEdits.current[mode]?.clear();
    cutMarks.current[mode] = [];
    sentMarks.current[mode] = '';
    const ver = editVerRef.current[mode] + 1;
    editVerRef.current = { ...editVerRef.current, [mode]: ver };
    setEditVer(editVerRef.current);
    setEditSent((v) => ({ ...v, [mode]: ver }));
  };
  // What was marked when the brush came out (the taps and the brush picture): Cancel puts it back.
  const brushFrom = useRef('');
  const brushSnap = useRef<ReturnType<CutEdit['snapshot']> | null>(null);
  const brushMarks = useRef<CutMark[]>([]);
  const brushRef = useRef(brush);
  brushRef.current = brush;
  const draftRef = useRef(paintDraft);
  draftRef.current = paintDraft;
  const brushSend = useRef<number | undefined>();
  // UNDO AND REDO OVER EVERYTHING DONE IN THE EDITOR (owner 2026-11-01): every choice (a slider's drag, changes under 0.6 s
  // apart, is one step) and every stroke of the brush, which lived outside the choices: with a stroke last, Undo did
  // nothing or took back something older. The automatic fill-ins are no step: there is nothing to see them take back.
  const hist = useRef({ past: [] as HistStep[], future: [] as HistStep[], at: 0, keys: '', prev: choices, cut: null as CutSave | null });
  const [, setHistTick] = useState(0);
  // How many steps stood when the brush came out: the brush's own Undo, and Cancel, go back no further.
  const brushBase = useRef(0);
  useEffect(() => {
    const h = hist.current;
    if (h.prev === choices) return;
    const changed = Object.keys({ ...h.prev, ...choices }).filter((k) => (h.prev[k] ?? '') !== (choices[k] ?? '') && k !== 'tap' && k !== 'replace');
    const filledIn = changed.length === 1 && changed[0] === 'songstart' && (!h.prev.songstart || !choices.songstart);
    if (changed.length && !filledIn) {
      const now = Date.now();
      const keys = changed.join(',');
      if (now - h.at > 600 || h.keys !== keys || !h.past.length) h.past = [...h.past.slice(-99), { choices: h.prev, ...(h.cut ? { cut: h.cut } : {}) }];
      h.at = now;
      h.keys = keys;
      h.future = [];
      h.cut = null;
    }
    h.prev = choices;
    setHistTick((n) => n + 1);
  }, [choices]);
  /** The choices now, the strokes on the brush not yet sent included. */
  const histNow = () => {
    const mode = brushRef.current.mode;
    return mode ? { ...hist.current.prev, [mode === 'keep' ? 'tap' : 'replace']: draftRef.current } : hist.current.prev;
  };
  const saveCut = (): CutSave => {
    const one = (mode: 'keep' | 'replace') => ({ edit: cutEdits.current[mode]?.lines ? cutEdits.current[mode]!.snapshot() : null, marks: cutMarks.current[mode].slice() });
    return { keep: one('keep'), replace: one('replace') };
  };
  const stepHist = useCallback((from: 'past' | 'future') => {
    const h = hist.current;
    const step = h[from][h[from].length - 1];
    if (!step) return;
    window.clearTimeout(brushSend.current);
    const here = histNow();
    const back: HistStep = { ...step, choices: here, ...(step.cut ? { cut: saveCut() } : {}) };
    if (from === 'past') {
      h.past = h.past.slice(0, -1);
      h.future = [...h.future, back];
    } else {
      h.future = h.future.slice(0, -1);
      h.past = [...h.past, back];
    }
    if (step.stroke) {
      const { mode, mark } = step.stroke;
      if (from === 'past') {
        cutMarks.current[mode] = cutMarks.current[mode].filter((m) => m.id !== mark.id);
        if (mark.line) cutEdits.current[mode]?.undo();
      } else {
        cutMarks.current[mode] = [...cutMarks.current[mode], mark];
        if (mark.line) cutEdits.current[mode]?.line(mark.line.pts, mark.line.erase, mark.line.r, mark.id);
      }
    }
    if (step.cut) {
      (['keep', 'replace'] as const).forEach((mode) => {
        const was = step.cut![mode];
        if (was.edit) cutEdits.current[mode]?.restore(was.edit);
        else cutEdits.current[mode]?.clear();
        cutMarks.current[mode] = was.marks.slice();
      });
    }
    // A choice taken back leaves the strokes as they stand.
    const marked = Boolean(step.stroke || step.cut);
    const next = marked ? step.choices : { ...step.choices, tap: here.tap ?? '', replace: here.replace ?? '' };
    h.prev = next;
    h.at = 0;
    setChoices(next);
    if (marked) {
      // The next check asks with the strokes as they stand now.
      sentMarks.current = { keep: marksOf('keep'), replace: marksOf('replace') };
      editVerRef.current = { keep: editVerRef.current.keep + 1, replace: editVerRef.current.replace + 1 };
      setEditVer(editVerRef.current);
      setEditSent(editVerRef.current);
    }
    const mode = brushRef.current.mode;
    if (mode && step.stroke) setPaintDraft(next[mode === 'keep' ? 'tap' : 'replace'] ?? '');
    else if (mode) setBrush({ mode: null, erase: false });
    setHistTick((n) => n + 1);
    // (Only refs and setters: the same from the first render on.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const undo = useCallback(() => stepHist('past'), [stepHist]);
  const redo = useCallback(() => stepHist('future'), [stepHist]);
  const undoCount = brush.mode ? Math.max(0, hist.current.past.length - brushBase.current) : 0;
  const brushStart = (mode: 'keep' | 'replace') => {
    brushFrom.current = mode === 'keep' ? tapAt : replaceAt;
    brushSnap.current = editOf(mode)?.snapshot() ?? null;
    brushMarks.current = cutMarks.current[mode].slice();
    brushBase.current = hist.current.past.length;
    // The numbers go on from the last tap's.
    const seen = (brushFrom.current.match(/#(\d+)/g) || []).map((t) => Number(t.slice(1)));
    cutSeq.current[mode] = Math.max(cutSeq.current[mode], ...seen, 0);
    setPaintDraft(brushFrom.current);
    setBrush({ mode, erase: false });
    setCropView('select');
    // Nothing on the picture may take the brush's touches (owner 2026-10-27: a placed backdrop "wont let you deselect").
    setBackdropFocus(null);
  };
  // Each stroke shows at once on the picture (CutLayer) and is sent by itself a moment after the last one (owner
  // 2026-10-28: no waiting for Done to see what a stroke did); Done only puts the brush down.
  const commitDraft = (mode: 'keep' | 'replace', draft: string, wait: number) => {
    window.clearTimeout(brushSend.current);
    brushSend.current = window.setTimeout(() => {
      sentMarks.current[mode] = marksOf(mode);
      setChoices((prev) => ((prev[mode === 'keep' ? 'tap' : 'replace'] ?? '') === draft ? prev : { ...prev, [mode === 'keep' ? 'tap' : 'replace']: draft }));
      setEditSent((v) => (v[mode] === editVerRef.current[mode] ? v : { ...v, [mode]: editVerRef.current[mode] }));
    }, wait);
  };
  // Taps kept in the text (a line costs nothing there: it goes into the brush picture). Far past any real cleanup.
  const MAX_STROKES = 1000;
  // The brush's size, the hand (a drag moves round the zoomed picture) and two fingers zooming (ZoomBox).
  const [cutSize, setCutSize] = useState<CutSize>('m');
  const [cutHand, setCutHand] = useState(false);
  const [pinching, setPinching] = useState(false);
  const editDraft = (next: (d: string) => string) => setPaintDraft((d) => {
    const n = next(d);
    if (brush.mode && n !== d) commitDraft(brush.mode, n, 700);
    return n;
  });
  /** The stroke goes on the list and is one step of Undo. */
  const markStroke = (mode: 'keep' | 'replace', mark: CutMark) => {
    const h = hist.current;
    if (h.past.length >= 100) brushBase.current = Math.max(0, brushBase.current - 1);
    h.past = [...h.past.slice(-99), { choices: histNow(), stroke: { mode, mark } }];
    h.future = [];
    h.at = 0;
    cutMarks.current[mode] = [...cutMarks.current[mode], mark];
    setHistTick((n) => n + 1);
  };
  // A stroke from the picture (CutLayer): a tap into the text with its number, a line into the brush picture.
  const addStroke = (st: string) => {
    const mode = brush.mode;
    const got = decodeStrokes(st)[0];
    if (!mode || !got) return;
    const seq = ++cutSeq.current[mode];
    const erase = Boolean(got.erase);
    if (got.length === 1) {
      if (paintDraft.split('|').filter(Boolean).length >= MAX_STROKES) return;
      const text = `${erase ? '-' : ''}#${seq} ${got[0][0].toFixed(3)},${got[0][1].toFixed(3)}`;
      markStroke(mode, { id: seq, text });
      editDraft((d) => [...(d ? d.split('|') : []), text].join('|'));
      return;
    }
    const edit = editOf(mode);
    if (!edit || !paintMap) return;
    // The brush's width, a share of the frame's short side, in the brush picture's pixels.
    const [x0] = paintMap.toFrame(0, 0);
    const [x1] = paintMap.toFrame(1, 1);
    const framePx = (Math.abs(x1 - x0) * mainSize.w) / edit.w;
    const r = ((got.r || CUT_BRUSH_R) * Math.min(mainSize.w, mainSize.h)) / Math.max(1e-6, framePx);
    markStroke(mode, { id: seq, text: st, line: { pts: got, erase, r } });
    edit.line(got, erase, r, seq);
    const ver = editVerRef.current[mode] + 1;
    editVerRef.current = { ...editVerRef.current, [mode]: ver };
    setEditVer(editVerRef.current);
    commitDraft(mode, paintDraft, 700);
  };
  const brushDone = () => {
    if (brush.mode) commitDraft(brush.mode, paintDraft, 0);
    setBrush({ mode: null, erase: false });
    // A backdrop is in place: back to it, with what was just fixed (useLiveCut).
    if (backdropOn) setCropView('result');
  };
  const brushCancel = () => {
    const mode = brush.mode;
    if (mode) {
      if (brushSnap.current) cutEdits.current[mode]?.restore(brushSnap.current);
      cutMarks.current[mode] = brushMarks.current.slice();
      hist.current.past = hist.current.past.slice(0, brushBase.current);
      hist.current.future = [];
      const ver = editVerRef.current[mode] + 1;
      editVerRef.current = { ...editVerRef.current, [mode]: ver };
      setEditVer(editVerRef.current);
      commitDraft(mode, brushFrom.current, 0);
    }
    setBrush({ mode: null, erase: false });
    if (backdropOn) setCropView('result');
  };
  // The shapes are placed round a lone tap on the subject; the crop brush's strokes never move them (and never ask again).
  const frameTap = /[\s|-]/.test(tapAt) ? '' : tapAt;
  const frameKeyOf = (s: ShapeKey) => `${askSubject}|${frameTap}|${places[s]?.mode === 'fill' ? places[s]!.pos.toFixed(3) : 'auto'}`;
  const frameNeed = coverPlan.shapes.filter((s) => framePrev?.keys[s] !== frameKeyOf(s));
  const frameAsk = frameNeed.map((s) => `${s}=${frameKeyOf(s)}`).join(',');
  useEffect(() => {
    if (tool !== 'canvas' || pickedKind !== 'image' || !firstPicked || !frameNeed.length) return undefined;
    const ac = new AbortController();
    setFrameBusy(true);
    const ask = frameNeed.slice();
    const keys = Object.fromEntries(ask.map((s) => [s, frameKeyOf(s)]));
    const fills = formatPlace(Object.fromEntries(ask.filter((s) => places[s]?.mode === 'fill').map((s) => [s, places[s]!])));
    const timer = window.setTimeout(() => {
      void getFramePreview(firstPicked, askSubject, ask, fills, ac.signal, frameTap).then((r) => {
        if (ac.signal.aborted) return;
        setFrameBusy(false);
        if (r) setFramePrev((prev) => ({ picture: r.picture, shapes: { ...(prev?.shapes || {}), ...r.shapes }, keys: { ...(prev?.keys || {}), ...keys } }));
      });
    }, 500);
    return () => {
      ac.abort();
      window.clearTimeout(timer);
      setFrameBusy(false);
    };
  }, [tool, pickedKind, firstPicked, frameAsk]); // eslint-disable-line react-hooks/exhaustive-deps
  // The main shape (the loop, the song versions, the page's copy): the live preview is drawn in it.
  const mainShape = coverPlan.main;
  const mainSize = sizeOf(mainShape, picDims);
  const mainPlace = places[mainShape];
  const mainPlaceStr = mainPlace ? formatPlace({ [mainShape]: { mode: mainPlace.mode, pos: Math.round(mainPlace.pos * 50) / 50 } }) : '';
  // The buyer's own window in the main shape, cut here as the server will cut it.
  const [mainCrop, setMainCrop] = useState<string | null>(null);
  useEffect(() => {
    setMainCrop(null);
    if (!pictureUrl || mainPlace?.mode !== 'crop') return undefined;
    let live = true;
    void cropStill(pictureUrl, mainSize, mainPlace.pos).then((u) => { if (live) setMainCrop(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [pictureUrl, mainPlace?.mode, mainPlace?.pos, mainSize.w, mainSize.h]); // eslint-disable-line react-hooks/exhaustive-deps
  // The last check stays on screen while the same picture is checked again (a new answer, a new shape): the steps it
  // decides (Crop, Sky or background) never vanish under the buyer's finger.
  const depthOf = useRef<File | null>(null);
  const cutCtx = `${askSubject}|${mainShape}|${mainPlaceStr}|${removeBg}`;
  useEffect(() => {
    if (depthOf.current !== firstPicked) setDepth(null);
    depthOf.current = firstPicked ?? null;
    setDepthBusy(false);
    setDepthFailed(false);
    if (tool !== 'canvas' || pickedKind !== 'image' || !firstPicked) return undefined;
    const ac = new AbortController();
    setDepthBusy(true);
    const asked = { ctx: cutCtx, keep: sentMarks.current.keep, replace: sentMarks.current.replace };
    const timer = window.setTimeout(() => {
      // A line brushed since is in the picture sent but not in `asked`: that answer shows, and is not kept for Undo.
      const exact = editVerRef.current.keep === editSent.keep && editVerRef.current.replace === editSent.replace;
      void editBlobs().then((edits) => getDepthPreview(firstPicked, askSubject, ac.signal, { shape: mainShape, place: mainPlaceStr }, tapAt, replaceAt, removeBg, edits)).then((d) => {
        if (ac.signal.aborted) return;
        // A check that could not run (busy, too many, failed on the server) keeps the last answer for this picture:
        // the sky and the subject it found stay found.
        if (d && d.why !== 'failed') {
          if (exact) {
            const keepAnswer = (mode: 'keep' | 'replace', mask: string | null) => {
              const list = [...cutAnswers.current[mode].filter((a) => a.ctx !== asked.ctx || a.ids !== asked[mode]), { ctx: asked.ctx, ids: asked[mode], mask }];
              cutAnswers.current[mode] = list.length > 40 ? [list[0], ...list.slice(-39)] : list;
            };
            keepAnswer('keep', d.depth && d.shape === mainShape ? d.mask ?? null : null);
            keepAnswer('replace', d.sky?.found && d.shape === mainShape ? d.sky.front ?? null : null);
          }
          setDepth(d);
        } else setDepthFailed(true);
        setDepthBusy(false);
      });
    }, 600);
    return () => {
      ac.abort();
      window.clearTimeout(timer);
    };
  }, [tool, pickedKind, firstPicked, askSubject, mainShape, mainPlaceStr, tapAt, replaceAt, removeBg, depthRetry, editSent.keep, editSent.replace]); // eslint-disable-line react-hooks/exhaustive-deps
  // THE NEW MOTION (owner 2026-10-23, living-motion.js): what is behind (keep, a new sky, a new background), the sky
  // moving, more stars, the object floating, the wind in the hair, the painted water and flags. What the page sends:
  const skyFound = Boolean(depth?.sky?.found);
  const behind: BehindMode = choices.behind === 'sky' && skyFound ? 'sky' : choices.behind === 'background' ? 'background' : 'keep';
  const skyClip = choices.skyclip && REPLACE_IDS.includes(choices.skyclip) ? choices.skyclip : '';
  const skyMove = skyFound && behind === 'keep' && choices.skymove !== 'off';
  const canFloat = subjectSaid === 'object' && Boolean(depth?.depth);
  const canHair = subjectSaid === 'person' && Boolean(depth?.depth && depth.face);
  // A subject in the distance (owner 2026-10-26: "Build the push-in, smoke and walk options only"): the camera pushes in
  // on anything in front; smoke and the walk across for a person or an animal that was cut out. Off until picked.
  const farFront = Boolean(depth) && subjectSaid !== 'scene' && distanceOf(choices.distance ?? '', depth) === 'far';
  const canPush = farFront;
  const canSmoke = farFront && subjectSaid === 'person' && Boolean(depth?.depth);
  // Only someone standing on ground that carries on beside them (owner 2026-10-26: the sitting man "floating in mid air").
  const canWalk = canSmoke && Boolean(depth?.walk);
  const motionFields: Record<string, string> = {
    sky: behind === 'sky' && skyClip ? skyClip : skyMove ? 'move' : '',
    stars: skyMove && choices.stars === 'on' ? 'on' : '',
    objectmotion: canFloat ? (choices.objectmotion || 'float') : '',
    hairwind: canHair ? (choices.hairwind || 'on') : '',
    push: canPush && choices.push === 'on' ? 'on' : '',
    smoke: canSmoke && choices.smoke === 'on' ? 'on' : '',
    walk: canWalk && choices.walk === 'on' ? 'on' : '',
    paint: pickedKind === 'image' ? choices.paint ?? '' : '',
    tap: tapAt,
    replace: behind === 'sky' && skyClip ? replaceAt : '',
  };
  const motionAny = Boolean(motionFields.sky || motionFields.objectmotion === 'float' || motionFields.hairwind === 'on' || motionFields.paint
    || motionFields.push || motionFields.smoke || motionFields.walk);
  // Anything in front seen from a distance (owner 2026-10-23 for a still object, 2026-10-26 for a person or an animal
  // too): it leans in less. What step 2 shows is what is sent: their answer, else far when the check found it small.
  const farOn = subjectSaid !== 'scene' && motionFields.objectmotion !== 'float' && distanceOf(choices.distance ?? '', depth) === 'far';
  const [painting, setPainting] = useState<PaintState>({ kind: null, brush: 'm', erase: false });
  // The new motion, previewed by the cover's own engine (canvas.js motionPreview): asked again once the choices settle.
  const [motionPrev, setMotionPrev] = useState<{ key: string; video: string | null } | null>(null);
  // Each preview video is a blob: link (getMotionPreview): the one replaced is let go.
  useEffect(() => () => { if (motionPrev?.video?.startsWith('blob:')) URL.revokeObjectURL(motionPrev.video); }, [motionPrev]);
  const [motionBusy, setMotionBusy] = useState(false);
  // What the Crop step marks now: the part to replace (a new sky or scene) or what to keep (a new background behind it).
  const selectWhich: 'keep' | 'replace' | null = brush.mode ?? (choices.behind === 'sky' ? 'replace' : choices.behind === 'background' ? 'keep' : null);
  // What stays after the cut, as a mask (white, its alpha what stays): the subject, or everything but the part replaced.
  const answeredMask = selectWhich === 'keep' ? (depth?.depth && depth.shape === mainShape ? depth.mask ?? null : null)
    : selectWhich === 'replace' ? (depth?.sky?.found && depth.shape === mainShape ? depth.sky.front ?? null : null) : null;
  // The longest answer the strokes standing start with, and the strokes after it: the page lays them on at once (CutLayer).
  const standing = selectWhich ? marksOf(selectWhich) : '';
  const cutBase = selectWhich ? cutAnswers.current[selectWhich].filter((a) => a.ctx === cutCtx && standing.startsWith(a.ids))
    .reduce<CutAnswer | null>((best, a) => (!best || a.ids.length >= best.ids.length ? a : best), null) : null;
  const keptMask = cutBase ? cutBase.mask : answeredMask;
  const cutPending = selectWhich ? cutMarks.current[selectWhich].slice(cutBase ? cutBase.ids.split(',').length - 1 : 0).map((m) => m.text).join('|') : '';
  // Taken back to an earlier answer: the last check's own mask is not what is on the stage.
  const cutMoved = Boolean(cutBase) && keptMask !== answeredMask;
  void editVer;
  // A check that could not run with strokes still waiting is asked again: the file and the engine's preview need them.
  useEffect(() => {
    if (!depthFailed || (!cutPending && !cutMoved)) return undefined;
    const t = window.setTimeout(() => setDepthRetry((n) => n + 1), 8000);
    return () => window.clearTimeout(t);
  }, [depthFailed, cutPending, cutMoved]);
  // No engine preview while the brush is out or strokes wait for their check: it would be drawn from an older cut, and
  // each one holds the server from the check the next stroke needs.
  const motionKey = motionAny && depth && !brush.mode && !cutPending && !cutMoved ? JSON.stringify([motionFields, subjectSaid, farOn, mainShape, mainPlaceStr, depth.token || '', backdropSent]) : '';
  useEffect(() => {
    if (tool !== 'canvas' || pickedKind !== 'image' || !firstPicked || !motionKey || !depth || painting.kind) {
      setMotionBusy(false);
      return undefined;
    }
    if (motionPrev?.key === motionKey) return undefined;
    const ac = new AbortController();
    setMotionBusy(true);
    const timer = window.setTimeout(() => {
      void editBlobs().then((edits) => getMotionPreview(firstPicked, { ...motionFields, subject: subjectSaid, distance: farOn ? 'far' : '', ...backdropSent }, depth.token, { shape: mainShape, place: mainPlaceStr }, ac.signal, edits)).then((m) => {
        if (ac.signal.aborted) return;
        setMotionBusy(false);
        setMotionPrev({ key: motionKey, video: m?.video ?? null });
      });
    }, 700);
    return () => {
      ac.abort();
      window.clearTimeout(timer);
    };
  }, [motionKey, painting.kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const motionShown = motionKey && motionPrev?.key === motionKey ? motionPrev.video : null;
  // A new sky shows at once while the engine draws it (browsing skies must be instant).
  const skyLive = behind === 'sky' && skyClip && depth?.sky?.front && !motionShown ? { front: depth.sky.front, clip: LIB_BACKDROPS[skyClip].file } : null;
  // Where the painted strokes go on the big picture (the main shape's placement, as the server places it).
  const paintMap = useMemo(() => (picDims ? frameMap(picDims, mainSize, (depth?.shape === mainShape ? depth?.placed : null) || framePrev?.shapes[mainShape]?.placed || mainPlace || null) : null),
    [picDims, mainSize.w, mainSize.h, depth?.shape, depth?.placed, framePrev, mainShape, mainPlace]); // eslint-disable-line react-hooks/exhaustive-deps
  // The live preview's box: the main shape for a picture, 9:16 otherwise.
  const previewAspect = pickedKind === 'image' ? `${mainSize.w} / ${mainSize.h}` : '9 / 16';
  // The picture the live preview shows: the main shape's, as the cover will be drawn from it.
  const mainStill = pickedKind === 'image' ? mainCrop || (depth?.shape === mainShape ? depth?.still : null) || framePrev?.shapes[mainShape]?.still || null : null;
  // The Living Cover's live preview stays in view whole once the customer has picked what to bring to life.
  const coverPinned = tool === 'canvas' && Boolean(firstPicked);
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 1023.98px)').matches);
  useEffect(() => {
    const mq = window.matchMedia?.('(max-width: 1023.98px)');
    if (!mq) return undefined;
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  useEffect(
    () => () => {
      if (pictureUrl) URL.revokeObjectURL(pictureUrl);
    },
    [pictureUrl],
  );
  // Re-read the form's switches for the live preview when one changes.
  const [, setFormTick] = useState(0);
  const [buying, setBuying] = useState(false);
  const [opKey] = useState(() => readOperatorKey(location.search));
  const [opCents, setOpCents] = useState(1500);
  const [qr, setQr] = useState<{ src: string; cents: number } | null>(null);
  const [opPaid, setOpPaid] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const readyRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<number | undefined>();
  const tickRef = useRef(0);

  // The server's price once it answers; its default until then (the same number unless an env var changed it).
  const price = money(config?.price_cents ?? DEFAULT_CENTS[tool]);
  // The Living Cover's ladder (owner 2026-10-22): the loop alone, with the 30 s song version, with the whole song, or
  // the Full pack (both song versions).
  const clipPrice = money(config?.clip_price_cents ?? 600);
  const fullPrice = money(config?.full_song_price_cents ?? 1000);
  const packPrice = money(config?.pack_price_cents ?? 1200);
  // What the Full pack saves against buying the 30 s and the whole song apart ("Best value · save $4").
  const packSave = money(Math.max(0, (config?.clip_price_cents ?? 600) + (config?.full_song_price_cents ?? 1000) - (config?.pack_price_cents ?? 1200)));
  // A personal design can have a song too (owner 2026-10-24: "that should be a part of that design 100 percent"):
  // with one it is priced by the song's options, like any cover; without, its one flat price.
  const songTier = hasSong ? (choices.length === 'full' || choices.length === 'pack' ? choices.length : 'clip') : null;
  const wholeSong = songTier === 'full' || songTier === 'pack';
  // Lyrics on screen: a +$5 add-on on any song version (owner 2026-10-22).
  const lyricsCents = config?.lyrics_price_cents ?? 500;
  const lyricsPrice = money(lyricsCents);
  const withLyrics = Boolean(hasSong && (choices.lyrics ?? '').trim());
  const tierCents = songTier === 'pack' ? config?.pack_price_cents ?? 1200 : songTier === 'full' ? config?.full_song_price_cents ?? 1000
    : songTier === 'clip' ? config?.clip_price_cents ?? 600 : config?.price_cents ?? DEFAULT_CENTS[tool];
  // Just a moving picture: one flat price (owner 2026-10-23, $3). An artist's platforms: the first ones free, then each
  // one more (owner 2026-10-23, "First 2 free, then +$1 each"), on any option. The server prices the job the same way.
  const pictureCents = config?.picture_price_cents ?? 300;
  const platformsFree = config?.platforms_free ?? 2;
  const extraPlatformCents = config?.extra_platform_price_cents ?? 100;
  const extraPlatforms = tool === 'canvas' && !pictureOnly ? Math.max(0, platformCount(sentPlatforms) - platformsFree) : 0;
  const chosenCents = pictureOnly && !hasSong ? pictureCents : tierCents + (withLyrics ? lyricsCents : 0) + extraPlatforms * extraPlatformCents;
  const chosenPrice = money(chosenCents);
  const optionName = (o: string) => t(`qt.tools.canvas.fields.length.options.${o}`, { price: '' }).split(' · ')[0];
  const priceLines: [string, number][] = tool !== 'canvas' ? [] : pictureOnly && !hasSong ? [[t('qt.tools.canvas.studio.price.picture'), pictureCents]] : [
    [songTier ? optionName(songTier) : t('qt.tools.canvas.studio.price.loop'), tierCents],
    ...(withLyrics ? [[t('qt.tools.canvas.studio.price.lyrics'), lyricsCents] as [string, number]] : []),
    ...(extraPlatforms > 0 ? [[t('qt.tools.canvas.studio.price.platforms', { count: extraPlatforms }), extraPlatforms * extraPlatformCents] as [string, number]] : []),
  ];
  // Once a job exists, its own price (the server set it from the choices).
  const payPrice = job ? money(job.price_cents) : chosenPrice;
  // Signed in with enough credits: the file is paid from the balance (owner
  // 2026-10-16: "it tries to force me to pay 3 dollars on stripe when i have
  // 89k credits"). Everyone else pays by card. Either way, before it is made.
  const { user, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  // REGISTERED FIRST (owner 2026-10-27: "a user shouldnt be able to use our tools without registering on our site
  // first. that would eliminate the issue that a user lost their entire creation"; he signed in halfway through a
  // Living Cover and lost it): signed out, every upload asks them to sign up or log in and brings them back to this
  // tool, before anything is made. The seller's in-person demos (operator key) and an order's own page stay open.
  const mustSignIn = !isAuthenticated && !opKey && !routeId;
  const signInFirst = () => {
    trackEvent('quicktool_sign_in_first', { tool });
    navigate(`/signup?redirect_to=${encodeURIComponent(location.pathname)}`);
  };
  const lockUpload = mustSignIn && !authLoading ? signInFirst : null;
  // Credits are 10 cents each, of the whole price (routes/quicktools.js creditsFor).
  const creditCost = job?.credits ?? Math.max(1, Math.ceil(chosenCents / 10));
  const balance = typeof user?.credits === 'number' ? user.credits : 0;
  const payByCredits = isAuthenticated && balance >= creditCost;
  const voiceOff = tool === 'faceless' && config?.voice_available === false;
  const uploads = useMemo(
    () =>
      (spec.uploads as UploadSpec[]).map((u) =>
        voiceOff && u.field === 'voiceover' ? { ...u, optional: false } : u,
      ),
    [spec.uploads, voiceOff],
  );
  const fields = useMemo(
    () => (spec.fields as FieldSpec[]).filter((f) => !(voiceOff && f.name === 'voice')),
    [spec.fields, voiceOff],
  );
  // A video's start time only for a video; the picture effects only for a picture.
  const shownFields = fields.filter((f) => {
    if (!f.when) return true;
    if (f.when === 'song') return hasSong;
    if (f.when === 'mark') return !!config?.watermark_available;
    return f.when === pickedKind || (f.when === 'image' && !pickedKind);
  });
  const hasForm = shownFields.length > 0 || uploads.length > 1;

  useEffect(() => {
    getQuickToolsConfig()
      .then((c) => setConfig(c[tool] ?? null))
      .catch(() => setConfig(null));
  }, [tool]);

  // The ?op= key is read once and dropped from the address bar.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!q.has('op')) return;
    q.delete('op');
    navigate({ pathname: location.pathname, search: q.toString() ? `?${q}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    trackPageView(`quicktool_${tool}`, { job: routeId ? 'yes' : 'no' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool]);

  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle(`${k('metaTitle')} | FaceShot-ChopShop`);
    return () => {
      document.title = previous;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, i18n.language]);

  const errorText = useCallback(
    (code: string | null | undefined, fallback?: string | null) =>
      code ? t(`qt.errors.${code}`, { defaultValue: fallback || t('qt.errors.server_error') }) : fallback || t('qt.errors.server_error'),
    [t],
  );

  const fail = useCallback(
    (message: string) => {
      setStage('upload');
      setProgress(null);
      setError(message);
      navigate(spec.path, { replace: true });
    },
    [navigate, spec.path],
  );

  // Follow the job in the URL until it is ready (or failed).
  const poll = useCallback(
    (id: string) => {
      window.clearTimeout(pollRef.current);
      getJob(tool, id, false, sessionId)
        .then(({ ok, job: j, err }) => {
          if (!ok || !j) return fail(err?.message ? errorText(err.error, err.message) : t('qt.errors.expired'));
          setJob(j);
          if (j.status === 'failed') return fail(errorText(j.error, j.message));
          if (j.status === 'awaiting_payment') {
            setStage('pay');
            // Back from Stripe a moment before it confirms: look again shortly.
            if (sessionId) pollRef.current = window.setTimeout(() => poll(id), 3000);
            return undefined;
          }
          if (j.status === 'ready') {
            setStage('ready');
            return undefined;
          }
          setStage('work');
          tickRef.current += 1;
          pollRef.current = window.setTimeout(() => poll(id), 4000);
          return undefined;
        })
        .catch(() => {
          pollRef.current = window.setTimeout(() => poll(id), 6000);
        });
    },
    [tool, fail, errorText, t, sessionId],
  );

  useEffect(() => {
    if (routeId) poll(routeId);
    return () => window.clearTimeout(pollRef.current);
  }, [routeId, poll]);

  // Ready: bring the preview into view once.
  const shownReady = useRef(false);
  useEffect(() => {
    if (stage !== 'ready' || shownReady.current) return;
    shownReady.current = true;
    readyRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [stage]);

  const send = useCallback(async () => {
    setError(null);
    const fd = new FormData();
    for (const u of uploads) {
      const files = picked[u.field] || [];
      if (!files.length && !u.optional) {
        setError(t('qt.errors.no_file'));
        return;
      }
      files.forEach((f) => fd.append(u.field, f));
    }
    const form = formRef.current;
    for (const f of fields) {
      // A slider is sent from what it shows (2026-10-20: it had no name, and an empty box of the same name was sent instead).
      if (f.type === 'range') {
        fd.append(f.name, choices[f.name] ?? f.default ?? '50');
        continue;
      }
      // Each overlay's see-through slider (owner 2026-10-21), "kind:60,...", from what the page shows.
      if (f.type === 'opacity') {
        fd.append(f.name, choices[f.name] ?? '');
        continue;
      }
      // The part of the song picked on its waveform (seconds), for the 30-second version.
      if (f.type === 'songstart') {
        fd.append(f.name, choices[f.name] ?? '');
        continue;
      }
      // Lyrics on screen and the release versions (owner 2026-10-22), from what the page shows; lyrics only with a song.
      if (f.type === 'lyrics') {
        fd.append(f.name, picked.song?.length ? choices[f.name] ?? '' : '');
        continue;
      }
      // What the picture is of (the check's answer unless the buyer changed it) and the new motion, from what the page shows.
      if (f.name === 'subject') {
        fd.append(f.name, subjectSaid || 'object');
        continue;
      }
      if (f.name in motionFields) {
        fd.append(f.name, motionFields[f.name]);
        continue;
      }
      // Where it will be posted (owner 2026-10-23): the places, the free shapes, the placements of the shapes made.
      if (f.name === 'purpose') {
        fd.append(f.name, purpose);
        continue;
      }
      if (f.name === 'platforms' || f.name === 'shapes' || f.name === 'place') {
        fd.append(f.name, f.name === 'platforms' ? sentPlatforms.join(',') : f.name === 'shapes' ? sentFree.join(',')
          : formatPlace(Object.fromEntries(coverPlan.shapes.filter((s) => places[s]).map((s) => [s, places[s]!]))));
        continue;
      }
      if (f.name === 'distance') {
        fd.append(f.name, farOn ? 'far' : '');
        continue;
      }
      if (f.name === 'bass') {
        fd.append(f.name, bassOn ? 'on' : 'off');
        continue;
      }
      if (f.type === 'release' || f.type === 'state') {
        // Release versions come with the Full pack only (owner 2026-10-22).
        fd.append(f.name, f.name === 'textlang' ? textLang : f.name === 'releaseday' ? choices[f.name] || 'fri'
          : f.name === 'release' && songTier !== 'pack' ? '' : choices[f.name] ?? '');
        continue;
      }
      // The extra formats (owner 2026-10-22, canvas-formats.js): all asked for; the server keeps what the option includes
      // (square and 4:5 with every option; YouTube, Apple Music and the 15 s / 60 s cuts with the Full pack). Never drawn as a box.
      if (f.type === 'formats') {
        fd.append(f.name, 'all');
        continue;
      }
      const el = form?.elements.namedItem(f.name) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (!el) continue;
      // A one-of choice is sent from what the page shows picked (owner 2026-10-17: picked the whole song, got 30 s).
      if (f.type === 'choice') {
        fd.append(f.name, choices[f.name] ?? f.default ?? f.options?.[0] ?? '');
        continue;
      }
      fd.append(f.name, f.type === 'checkbox' ? ((el as HTMLInputElement).checked ? 'on' : 'off') : el.value);
    }
    setProgress(0);
    trackEvent('quicktool_upload_started', { tool });
    if (opKey) fd.append('operator_key', opKey);
    // The crop brush's pictures: the file is cut exactly as the preview was (owner 2026-10-28).
    if (tool === 'canvas' && pickedKind === 'image') {
      const edits = await editBlobs();
      if (edits.tap && fd.get('tap') !== null) fd.append('tapedit', edits.tap, 'tapedit.png');
      if (edits.replace && fd.get('replace') !== null) fd.append('replaceedit', edits.replace, 'replaceedit.png');
    }
    const { ok, job: j, err } = await uploadJob(tool, fd, setProgress);
    if (!ok || !j) {
      setProgress(null);
      setError(errorText(err?.error, err?.message));
      return;
    }
    rememberQuickJob(tool, j.id);
    setProgress(null);
    setJob(j);
    navigate(`${spec.path}/${j.id}`);
    if (j.status === 'awaiting_payment') await payFor(j.id);
    else setStage('work');
  }, [uploads, fields, picked, choices, tool, navigate, spec.path, errorText, t, payByCredits, textLang, sentPlatforms, sentFree, coverPlan, places, subjectSaid, motionFields.sky, motionFields.stars, motionFields.objectmotion, motionFields.hairwind, motionFields.push, motionFields.smoke, motionFields.walk, motionFields.paint, purpose, pictureOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paid and marked: make it again without the site mark, free (owner 2026-10-17).
  const [unmarking, setUnmarking] = useState(false);
  async function unmark() {
    if (!job) return;
    setUnmarking(true);
    setError(null);
    const r = await remakeWithoutMark(tool, job.id);
    setUnmarking(false);
    if (!r.ok || !r.job) {
      setError(errorText(r.err?.error, r.err?.message));
      return;
    }
    setJob(r.job);
    setStage('work');
    poll(job.id);
  }

  // Pay for an uploaded file: credits when the balance covers it, else Stripe.
  async function payFor(id: string) {
    setBuying(true);
    setError(null);
    const token = api.getToken();
    if (payByCredits && token) {
      trackEvent('quicktool_paid_credits_clicked', { tool });
      const r = await payWithCredits(tool, id, token);
      if (r.ok && r.job) {
        setBuying(false);
        setJob(r.job);
        setStage('work');
        void refreshUser?.();
        poll(id);
        return;
      }
      if (r.err?.error !== 'insufficient_credits') {
        setBuying(false);
        setStage('pay');
        setError(errorText(r.err?.error, r.err?.message));
        return;
      }
    }
    trackEvent('quicktool_checkout_clicked', { tool });
    const c = await startCheckout(tool, id);
    if (c.url) {
      window.location.href = c.url;
      return;
    }
    setBuying(false);
    setStage('pay');
    setError(c.message || t('qt.errors.checkout_failed'));
  }

  const onPick = (field: string, files: FileList | null) => {
    if (mustSignIn) {
      signInFirst();
      return;
    }
    const list = files ? Array.from(files) : [];
    setPicked((prev) => ({ ...prev, [field]: list }));
    // One upload and nothing to type: go straight away (one tap to a preview).
    if (!hasForm && list.length) window.setTimeout(() => formRef.current?.requestSubmit(), 0);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };

  const buy = async () => {
    if (!job) return;
    setBuying(true);
    trackEvent('quicktool_checkout_clicked', { tool });
    const r = await startCheckout(tool, job.id);
    if (r.url) {
      window.location.href = r.url;
      return;
    }
    if (r.already_paid) {
      window.location.reload();
      return;
    }
    setBuying(false);
    setError(r.message || t('qt.errors.checkout_failed'));
  };

  // In person: a QR of the Checkout link; wait for Stripe to say paid.
  const showQr = async () => {
    if (!job || !opKey) return;
    const r = await startCheckout(tool, job.id, { operator_key: opKey, price_cents: opCents });
    if (!r.qr) {
      setError(r.message || t('qt.errors.checkout_failed'));
      return;
    }
    setQr({ src: r.qr, cents: r.amount_cents || opCents });
    const wait = () => {
      getJob(tool, job.id, true)
        .then(({ job: j }) => {
          if (j?.paid) {
            setOpPaid(true);
            setQr(null);
            return;
          }
          pollRef.current = window.setTimeout(wait, 3000);
        })
        .catch(() => {
          pollRef.current = window.setTimeout(wait, 5000);
        });
    };
    window.clearTimeout(pollRef.current);
    wait();
  };

  const paid = !!job && (job.paid || !!sessionId);

  const sample = spec.sample;
  const creator = spec.audience === 'creator';
  // The Living Cover: the examples the owner picked, and what the live preview draws.
  const [examples, setExamples] = useCoverExamples();
  // The owner's examples play in the preview until a picture is picked: on a phone that preview leads the page.
  const reelOn = tool === 'canvas' && !firstPicked && Boolean(examples && examples.length);
  const floatPicked = (choices.float ?? '').split(',').filter((v) => v && v !== 'none');
  // The pasted lyrics roughly timed for the live preview (the 30 s part picked, or the song's start for a whole song).
  const lyricText = hasSong ? choices.lyrics ?? '' : '';
  const lyricLines = useMemo(
    () => (lyricText.trim() ? previewLyricLines(lyricText, songSeconds || 30, songTier === 'full' ? 0 : Number(choices.songstart) || 0, 30) : []),
    [lyricText, songSeconds, songTier, choices.songstart],
  );
  const releasePicked = (songTier === 'pack' ? (choices.release ?? '').split(',').filter(Boolean) : []) as ReleaseKind[];
  // THE BASS SHAKE (owner 2026-10-27: "it would be better to have the bass image be a option that is not mandatory but
  // instead a option for the user to choose. also it would be better if that happened at the same time they choose their
  // song... that way they can choose the section with the bass effect they want and see it throughout the creation"):
  // off unless picked, picked in the Song step, and the picture shakes to the part picked in every step while the song
  // plays (the play button in the top bar, or the part picker's own), worked out with the file's numbers (bass-level.ts).
  const bassOn = hasSong && choices.bass === 'on';
  const songLen = songTier === 'full' ? Math.min(600, songSeconds || 600) : 30;
  const songFrom = songTier === 'full' ? 0 : Number(choices.songstart) || 0;
  const [bassLevels, setBassLevels] = useState<Float32Array | null>(null);
  useEffect(() => {
    setBassLevels(null);
    const f = picked.song?.[0];
    if (!bassOn || !f) return undefined;
    let live = true;
    const timer = window.setTimeout(() => {
      void songBassLevel(f, songFrom, songLen).then((l) => { if (live) setBassLevels(l); }).catch(() => undefined);
    }, 300);
    return () => { live = false; window.clearTimeout(timer); };
  }, [bassOn, picked.song, songFrom, songLen]);
  // The song over the picture, from the part picked, that part looping.
  const stageAudio = useRef<HTMLAudioElement | null>(null);
  const [stagePlaying, setStagePlaying] = useState(false);
  const songUrl = useMemo(() => (picked.song?.[0] ? URL.createObjectURL(picked.song[0]) : null), [picked.song]);
  useEffect(() => () => { if (songUrl) URL.revokeObjectURL(songUrl); }, [songUrl]);
  useEffect(() => {
    stageAudio.current?.pause();
    setStagePlaying(false);
  }, [songUrl, songFrom, songLen]);
  const toggleStageSong = () => {
    const a = stageAudio.current;
    if (!a || !songUrl) return;
    if (!a.paused) {
      a.pause();
      setStagePlaying(false);
      setLyricTime(null);
      return;
    }
    a.currentTime = songFrom;
    void a.play().then(() => setStagePlaying(true)).catch(() => undefined);
  };
  const lyricTimeRef = useRef<number | null>(null);
  lyricTimeRef.current = lyricTime;
  const bassFeed = useMemo(() => (bassOn && bassLevels ? {
    level: bassLevels,
    at: () => {
      const a = stageAudio.current;
      if (a && !a.paused) return a.currentTime - songFrom;
      return lyricTimeRef.current;
    },
  } : null), [bassOn, bassLevels, songFrom]);
  const songName = (formRef.current?.elements.namedItem('songname') as HTMLInputElement | null)?.value ?? '';
  const coverOpacity = useMemo(() => Object.fromEntries(Object.entries(parseOpacity(choices.opacity ?? '')).map(([k, n]) => [k, n / 100])), [choices.opacity]);
  const coverSpot = useMemo(() => parseSpot(choices.spot ?? ''), [choices.spot]);
  // The backdrops as the preview lays them (LivingCover backdrops), bottom first, each with its place and slider.
  const backSpot = useMemo(() => parseSpot(choices.bspot ?? ''), [choices.bspot]);
  const backSee = useMemo(() => parseOpacity(choices.bopacity ?? ''), [choices.bopacity]);
  const coverBackdrops = useMemo(() => backdropIds(choices.backdrop).map((id) => ({ src: LIB_BACKDROPS[id].file, place: backSpot[id] ?? null, opacity: (backSee[id] ?? 100) / 100 })),
    [choices.backdrop, backSpot, backSee]);
  // The backdrop being placed on the picture (moved and resized, like an overlay).
  const [backdropFocus, setBackdropFocus] = useState<string | null>(null);
  // The overlay being placed on the picture (its opacity slider open, its outline and corners on the preview).
  const [overlayFocus, setOverlayFocus] = useState<string | null>(null);
  // THE LIVING COVER EDITOR (owner 2026-10-21: "it must look like CapCut / TikTok / Instagram's editors, not a web
  // form"): once a picture is picked it fills a dark stage under the site's bar; a toolbar (phone: along the bottom,
  // laptop: a rail on the left) opens one tool at a time (phone: a sheet over the bottom, laptop: a panel beside the
  // picture); Export sits top right. Undo/redo (CapCut) and hold-to-compare (Instagram) work on the picture.
  const studio = tool === 'canvas';
  const switchFields = shownFields.filter((f) => !f.example && f.when && f.type === 'checkbox');
  // The switches are kept in `choices` (so undo covers them); their checkboxes show what is there.
  const isOn = (f: FieldSpec) => (choices[f.name] ?? (f.checked ? 'on' : 'off')) === 'on';
  const setOn = (f: FieldSpec, on: boolean) => setChoices((prev) => ({ ...prev, [f.name]: on ? 'on' : 'off' }));
  const bwOn = studio ? (choices.bw ?? 'off') === 'on' : Boolean((formRef.current?.elements.namedItem('bw') as HTMLInputElement | null)?.checked);
  const [editorOpen, setEditorOpen] = useState(true);
  const editorShown = studio && stage === 'upload' && Boolean(firstPicked) && editorOpen;
  const [pane, setPane] = useState<EditorPane | null>(null);
  const [slider, setSlider] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);
  const compareTimer = useRef<number | undefined>();
  const grabY = useRef<number | null>(null);
  useEffect(() => {
    if (!firstPicked) return;
    setEditorOpen(true);
    // Step 1 opens by itself (owner 2026-10-21: "i just sat there for awhile not knowing what i was suppose to do next").
    setPane((p) => p ?? (firstPicked.type.startsWith('image') ? 'post' : 'found'));
  }, [firstPicked]);
  // The page under the editor stays still.
  useEffect(() => {
    if (!editorShown) return undefined;
    const html = document.documentElement;
    const before = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      html.style.overflow = before;
    };
  }, [editorShown]);
  useEffect(() => {
    if (!editorShown) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'range', 'file'].includes((el as HTMLInputElement).type)))) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      } else if (e.key === 'Escape') {
        setSlider(null);
        setPane(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editorShown, undo, redo]);
  // Something went wrong while sending: show it where the Export button is.
  useEffect(() => {
    if (error && editorShown) setPane('finish');
  }, [error, editorShown]);
  const bf = shownFields.find((f) => f.type === 'backdrop');
  // A new background needs something cut out in front of it: never for a scene.
  const canBackdrop = Boolean(bf) && Object.keys(LIB_BACKDROPS).length > 0 && subjectSaid !== 'scene' && Boolean(depth?.depth);
  const of = shownFields.find((f) => f.type === 'multi');
  const rangeFields = shownFields.filter((f) => f.type === 'range');
  // Fabric motion is the old whole-picture ripple: the new motion draws the picture another way and leaves it out.
  const lookSwitches = switchFields.filter((f) => f.name !== 'watermark' && !(motionAny && f.name === 'cloth'));
  const markSwitch = switchFields.find((f) => f.name === 'watermark');
  // THE ORDER (owner 2026-10-23: "the first question the user should answer is if they are uploading a cover to spotify,
  // tiktok, youtube, soundcloud etc. than we ask if the subject of their image is alive ... than finally we ask if they
  // want a full crop ... a partial crop ... or no crop ... that will determine if they go to the backdrop step or
  // straight to the overlay after picking their song. they need to choose that before the last steps so they can see how
  // their song affects the creation").
  const panes = ([
    // 1. Where it goes: it sets the shapes everything after is drawn in.
    ['post', Smartphone, tool === 'canvas' && pickedKind === 'image'],
    // 2. What is in it: a video's clip and where it starts. A picture is not asked (owner 2026-10-27: "maybe we just need to
    // really simplify this ... that decision is your"): the picture check finds what is in it, the Crop step's brush
    // says what to keep or replace, and Make it move says what moves and how.
    ['found', ScanSearch, pickedKind !== 'image'],
    // 3. The crop: none, a new sky, a full cutout (a tile shows only when it can work; owner 2026-10-21: "a backdrop
    // shouldnt be a option if it is not applicable").
    // Always there for a picture (owner 2026-10-24: "the backdrop and cropping steps just disappeared"); a choice that
    // cannot work on this picture says why and what to do instead of vanishing.
    ['crop', Crop, pickedKind === 'image'],
    // 4. The song, before the last steps, so every one after shows how it moves the picture.
    ['song', Music, uploads.some((u) => u.optional)],
    ['move', Wind, pickedKind === 'image'],
    ['overlays', Sparkles, pickedKind === 'image' && Boolean(of)],
  ] as [EditorPane, LucideIcon, boolean][]).filter(([, , on]) => on);
  const openPane = pane && (pane === 'finish' || panes.some(([id]) => id === pane)) ? pane : null;
  // The laptop always shows a panel; the phone's sheet can be closed to see the picture whole.
  const shownPane: EditorPane | null = openPane ?? (phone ? null : panes[0]?.[0] ?? 'found');
  // GUIDED STEPS (owner 2026-10-21: "it should be guided and instructed and obvious what a user is suppose to do for
  // each step ... putting each next step right in front of their face"): every tool is a numbered step with one line
  // saying what to do, and a big Next button that is always in view; the steps already seen get a check.
  const steps: EditorPane[] = [...panes.map(([id]) => id), 'finish'];
  const songSteps = steps.includes('song');
  const stepAt = shownPane ? steps.indexOf(shownPane) : -1;
  const [seen, setSeen] = useState<EditorPane[]>([]);
  useEffect(() => {
    if (shownPane && !seen.includes(shownPane)) setSeen((v) => [...v, shownPane]);
  }, [shownPane, seen]);
  useEffect(() => {
    if (openPane !== 'move' && painting.kind) setPainting((p) => ({ ...p, kind: null, erase: false }));
  }, [openPane, painting.kind]);
  // The crop brush is put down when its step is left.
  useEffect(() => {
    if (openPane !== 'crop' && brush.mode) setBrush({ mode: null, erase: false });
  }, [openPane, brush.mode]);
  // Removing a part with no sky found once the check is done, and nothing marked yet: the brush comes out by itself so
  // one tap marks it (never while the check is still looking: its sky may be on the way).
  useEffect(() => {
    if (openPane === 'crop' && choices.behind === 'sky' && depth && !depthBusy && !skyFound && !replaceAt && !brush.mode) brushStart('replace');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPane, choices.behind, depth, depthBusy, skyFound, replaceAt]);
  const goStep = (i: number) => {
    setSlider(null);
    setPane(steps[Math.max(0, Math.min(steps.length - 1, i))]);
  };
  // The price is always in dollars, and in credits too when paid from the balance (owner 2026-10-23: "I also did not
  // seen any option to choose what i wanted or the price difference it just charged me a flat 60").
  const exportLabel = payByCredits ? k('studio.exportCredits', { credits: creditCost.toLocaleString(), price: chosenPrice }) : k('studio.export', { price: chosenPrice });
  const startCompare = () => {
    window.clearTimeout(compareTimer.current);
    compareTimer.current = window.setTimeout(() => setCompare(true), 220);
  };
  const endCompare = () => {
    window.clearTimeout(compareTimer.current);
    setCompare(false);
  };
  // SWIPE TO TRY OVERLAYS (owner 2026-10-21: "implement the swipe feature like snapochat to quickly see the overlays on
  // the image"): on the Overlays step a swipe across the picture puts the next (or previous) overlay on it, like a
  // Snapchat lens; the swiped one keeps one of the picks, the ones tapped in the tray stay.
  const overlayItems = useOverlayItems(tool, of ?? NO_MULTI);
  const [swipeId, setSwipeId] = useState<string | null>(null);
  const [swipeTip, setSwipeTip] = useState<string | null>(null);
  const swipeX = useRef<number | null>(null);
  const tipTimer = useRef<number | undefined>();
  const showTip = (text: string) => {
    setSwipeTip(text);
    window.clearTimeout(tipTimer.current);
    tipTimer.current = window.setTimeout(() => setSwipeTip(null), 1600);
  };
  const swipeTo = (dir: 1 | -1) => {
    if (!of || !overlayItems.length) return;
    const max = of.picks || 3;
    const kept = floatPicked.filter((id) => id !== swipeId);
    if (kept.length >= max) {
      showTip(k('studio.swipeFull', { max }));
      return;
    }
    let at = swipeId ? overlayItems.findIndex((i) => i.id === swipeId) : dir === 1 ? -1 : 0;
    let next = overlayItems[0];
    for (let n = 0; n < overlayItems.length; n += 1) {
      at = (at + dir + overlayItems.length) % overlayItems.length;
      next = overlayItems[at];
      if (!kept.includes(next.id)) break;
    }
    const list = [...kept, next.id];
    setChoices((prev) => ({ ...prev, [of.name]: list.join(','), ...keepPicked(prev, list) }));
    setSwipeId(next.id);
    showTip(next.label.replace(/\s+\d+$/, ''));
  };
  const swiping = openPane === 'overlays' && pickedKind === 'image' && Boolean(of);
  // Placing one overlay on the picture (owner 2026-10-24: moved and resized like InShot's): its outline and corners
  // show and the picture's swipe and compare wait until it is done.
  const placing = swiping && overlayFocus && floatPicked.includes(overlayFocus) ? overlayFocus : null;
  // A backdrop picked to place (owner 2026-10-27: "the backdrops also should be able to be modified the same").
  const selecting = openPane === 'crop' && pickedKind === 'image' && selectWhich !== null && (brush.mode !== null || cropView === 'select');
  const brushAdds = brush.mode ? strokeAdds(brush.mode, brush.erase) : true;
  const placingBack = !selecting && openPane === 'crop' && pickedKind === 'image' && backdropOn && backdropFocus && backdropIds(choices.backdrop).includes(backdropFocus) ? backdropFocus : null;
  const overlayName = (id: string) => (overlayItems.find((i) => i.id === id)?.label || id).replace(/\s+\d+$/, '');
  const downAt = useRef(0);
  // The cut drawn on the stage only when it was made for the shape on screen (owner 2026-10-28: a backdrop "appears
  // inaccurately"): a cut kept from another shape's check lies in the wrong place over the backdrop.
  const madeDepth = depth && (!depth.shape || depth.shape === mainShape) ? depth : null;
  // The stage shows the cut as the buyer left it, the strokes waiting for their check included.
  const liveCut = useLiveCut(selecting ? null : keptMask, cutPending, selectWhich, paintMap, cutMoved);
  const stageDepth = madeDepth && liveCut && selectWhich === 'keep' ? { ...madeDepth, ...liveCut } : madeDepth;
  // With the background removed the stage is the sharp picture through its own mask on every step (owner 2026-11-01: "once
  // you add your backdrop it does another auto crop that inaccurately crops away things it shouldnt"): the engine's loop,
  // a quarter of the frame's size, read as a worse crop on each step after this one, and with the phone's sheet closed.
  // Only Make it move shows it, where the motion is what is being looked at, and it says what it is.
  const motionForCut = cutOn && Boolean(stageDepth?.depth);
  const stageMotion = motionForCut && openPane !== 'move' ? null : motionShown;
  const livingCover = (className: string, style?: CSSProperties) => (
    <LivingCover
      style={style}
      picture={mainStill || pictureUrl || undefined}
      frame={pickedKind === 'image' && (mainSize.w !== 1080 || mainSize.h !== 1920) ? mainSize : null}
      pictureFilter={bwOn ? 'grayscale(1) contrast(1.15)' : undefined}
      kinds={pickedKind === 'video' ? [] : floatPicked}
      amount={choices.amount ?? '50'}
      opacity={coverOpacity}
      spot={coverSpot}
      glow={choices.glowlevel ?? '50'}
      brightness={pickedKind === 'image' ? choices.brightness ?? '50' : '50'}
      depth={pickedKind === 'image' ? stageDepth : null}
      bass={pickedKind === 'image' ? bassFeed : null}
      dark={pickedKind === 'image' ? depth?.dark ?? 0 : 0}
      far={farOn}
      backdrops={pickedKind === 'image' && backdropOn ? coverBackdrops : null}
      motion={pickedKind === 'image' ? stageMotion : null}
      skyLive={pickedKind === 'image' ? (skyLive && liveCut && selectWhich === 'replace' ? { ...skyLive, front: liveCut.mask } : skyLive) : null}
      cutOut={pickedKind === 'image' && cutOn && Boolean(stageDepth?.depth)}
      className={className}
    >
      {!pictureUrl && (
        <p className="pointer-events-none absolute inset-x-6 top-1/2 -translate-y-1/2 text-center text-[15px] font-semibold text-white/75">{k('previewEmpty')}</p>
      )}
    </LivingCover>
  );
  const paneBody: Record<EditorPane, ReactNode> = {
    post: (
      <>
      {/* The picture itself, to change it (step 2 used to hold it; owner 2026-10-27 simplified the questions away). */}
      <div className="mb-4">
        {uploads.filter((u) => !u.optional).map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} studio />)}
      </div>
      <CoverShapes
        purpose={purpose}
        onPurpose={(v) => setChoices((prev) => ({ ...prev, purpose: v }))}
        platforms={platformsPicked}
        free={freePicked}
        onPlatforms={(v) => setChoices((prev) => ({ ...prev, platforms: v.join(',') }))}
        onFree={(v) => setChoices((prev) => ({ ...prev, shapes: v.join(',') }))}
        shapes={coverPlan.shapes}
        dropped={coverPlan.dropped as ShapeKey[]}
        prices={{ picture: money(pictureCents), free: platformsFree, each: money(extraPlatformCents), extra: extraPlatforms, extraTotal: money(extraPlatforms * extraPlatformCents) }}
        picture={pictureUrl && picDims ? { url: pictureUrl, ...picDims } : null}
        previews={framePrev?.shapes ?? null}
        busy={frameBusy}
        places={places}
        onPlace={setPlace}
      />
      {/* The song from the start (owner 2026-10-24: "the music option is to be in the beginning"; it used to vanish once
          a picture was added and come back only at the Song step). Its part and its version are picked there. */}
      {tool === 'canvas' && uploads.some((u) => u.optional) && (
        <div className="mt-5" data-testid="qt-song-early">
          {uploads.filter((u) => u.optional).map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} studio />)}
          <p className="mt-1.5 text-[12.5px] leading-snug text-muted-foreground">{k(hasSong ? 'studio.songLater' : 'studio.songAnyTime')}</p>
        </div>
      )}
      </>
    ),
    found: (
      <>
        <div className="mt-4">
          {uploads.filter((u) => !u.optional).map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} studio />)}
        </div>
        {shownFields.filter((f) => f.type === 'number').map((f) => <FieldInput key={f.name} tool={tool} field={f} short />)}
      </>
    ),
    crop: (
      <BehindStep
        mode={choices.behind === 'sky' ? 'sky' : behind}
        onMode={(m) => {
          // Each choice starts from its own automatic cut: what was marked for another one is let go (owner 2026-10-28: "Background
          // removed around what you marked" showed though nothing had been marked).
          if (choices.behind !== m) {
            hist.current.cut = saveCut();
            clearEdit('keep');
            clearEdit('replace');
          }
          setChoices((prev) => (prev.behind === m ? prev : { ...prev, behind: m, ...(m === 'background' ? { cutout: 'on' } : { cutout: '', backdrop: '' }), tap: '', replace: '' }));
          // Nothing found to keep or to replace: the brush comes out at once, so they paint it.
          setBrush({ mode: null, erase: false });
          setCropView('select');
        }}
        // The whole picture in the small tiles (the tall still is mostly sky at that size).
        photo={pictureUrl || mainStill}
        skyFound={skyFound}
        canBackdrop={canBackdrop}
        checking={!depth && depthBusy}
        checkFailed={!depth && depthFailed}
        onRetry={() => setDepthRetry((n) => n + 1)}
        paint={{
          painting: brush.mode, erase: brush.erase, busy: depthBusy, draft: undoCount ? 'x' : '',
          saved: { keep: tapAt || (cutEdits.current.keep?.lines ? 'E' : ''), replace: replaceAt || (cutEdits.current.replace?.lines ? 'E' : '') },
          onStart: brushStart, onErase: (on) => { setCutHand(false); setBrush((b) => ({ ...b, erase: on })); }, onDone: brushDone,
          onUndo: undo,
          onCancel: brushCancel,
          full: paintDraft.split('|').filter(Boolean).length >= MAX_STROKES,
          size: cutSize, onSize: (z) => { setCutHand(false); setCutSize(z); },
          onClear: (which) => {
            const h = hist.current;
            h.past = [...h.past.slice(-99), { choices: histNow(), cut: saveCut() }];
            h.future = [];
            h.at = 0;
            clearEdit(which); setChoices((prev) => ({ ...prev, [which === 'keep' ? 'tap' : 'replace']: '' })); },
        }}
        view={cropView}
        onView={setCropView}
        skyClip={skyClip}
        onSkyClip={(id) => { setChoices((prev) => ({ ...prev, skyclip: id })); if (id) setCropView('result'); }}
        skyMove={skyMove}
        onSkyMove={(on) => setChoices((prev) => ({ ...prev, skymove: on ? 'on' : 'off' }))}
        stars={choices.stars === 'on'}
        onStars={(on) => setChoices((prev) => ({ ...prev, stars: on ? 'on' : 'off' }))}
        backdrop={bf && (
          <BackdropField bare tool={tool} field={bf} value={choices[bf.name] ?? ''} onChange={(v) => { setChoices((prev) => ({ ...prev, [bf.name]: v })); if (v) setCropView('result'); }}
            subject={subjectSaid} depth={depth} depthBusy={depthBusy} picked={Boolean(firstPicked)} cut={cutOn} onCut={setCut} declined={choices.cutout === 'no'} photo={pictureUrl}
            opacity={choices.bopacity ?? ''} onOpacity={(v) => setChoices((prev) => ({ ...prev, bopacity: v }))}
            spot={choices.bspot ?? ''} onSpot={(v) => setChoices((prev) => ({ ...prev, bspot: v }))} focus={backdropFocus} onFocus={(id) => { setBackdropFocus(id); if (id) { setCropView('result'); setBrush({ mode: null, erase: false }); } }} />
        )}
      />
    ),
    move: (
      <MoveStep
        canFloat={canFloat}
        floatOn={motionFields.objectmotion === 'float'}
        onFloat={(on) => setChoices((prev) => ({ ...prev, objectmotion: on ? 'float' : 'lean' }))}
        canHair={canHair}
        hairOn={motionFields.hairwind === 'on'}
        onHair={(on) => setChoices((prev) => ({ ...prev, hairwind: on ? 'on' : 'off' }))}
        far={{ push: canPush, smoke: canSmoke, walk: canWalk }}
        farOn={{ push: motionFields.push === 'on', smoke: motionFields.smoke === 'on', walk: motionFields.walk === 'on' }}
        onFar={(k, on) => setChoices((prev) => ({ ...prev, [k]: on ? 'on' : 'off' }))}
        paint={choices.paint ?? ''}
        onPaint={(v) => setChoices((prev) => ({ ...prev, paint: v }))}
        painting={painting}
        onPainting={setPainting}
        busy={motionBusy}
      />
    ),
    overlays: of && (
      <MultiField bare tool={tool} field={of} value={choices[of.name] ?? of.default ?? ''} onChange={(v) => setChoices((prev) => ({ ...prev, [of.name]: v }))}
        opacity={choices.opacity ?? ''} onOpacity={(v) => setChoices((prev) => ({ ...prev, opacity: v }))}
        focus={overlayFocus} onFocus={setOverlayFocus} spot={choices.spot ?? ''} onSpot={(v) => setChoices((prev) => ({ ...prev, spot: v }))} />
    ),
    song: (
      <>
        {uploads.filter((u) => u.optional).map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} studio />)}
        {/* What they get with their song, each with its price, picked here and not at the end (owner 2026-10-23: "I also
            did not seen any option to choose what i wanted or the price difference"). */}
        {hasSong && shownFields.filter((f) => f.name === 'length').map((f) => (
          <ChoiceField key={f.name} tool={tool} field={f} value={choices[f.name] ?? f.default ?? f.options?.[0] ?? ''}
            onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))} prices={{ clip: clipPrice, full: fullPrice, pack: packPrice, packSave }} />
        ))}
        {/* The bass shake: the buyer's choice, with the song (owner 2026-10-27). */}
        {hasSong && (
          <div className="mt-4 rounded-2xl bg-white/[0.05] p-3" data-testid="qt-bass">
            <button type="button" role="switch" aria-checked={bassOn} onClick={() => setChoices((prev) => ({ ...prev, bass: bassOn ? 'off' : 'on' }))} data-testid="qt-bass-switch"
              className="flex min-h-[48px] w-full items-center gap-3 text-left">
              <AudioWaveform className={cn('h-5 w-5 shrink-0', bassOn ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block text-[14.5px] font-bold text-foreground">{k('studio.bass.title')}</span>
                <span className="block text-[12px] leading-snug text-muted-foreground">{k('studio.bass.hint')}</span>
              </span>
              <span aria-hidden className={cn('relative h-6 w-10 shrink-0 rounded-full transition-colors', bassOn ? 'bg-primary' : 'bg-white/15')}>
                <i className={cn('absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform', bassOn && 'translate-x-4')} />
              </span>
            </button>
            {bassOn && (
              <button type="button" onClick={toggleStageSong} className="mt-2 flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-white/[0.08] text-[13.5px] font-semibold text-foreground" data-testid="qt-bass-play">
                {stagePlaying ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
                {k(stagePlaying ? 'studio.songStop' : 'studio.bass.see')}
              </button>
            )}
          </div>
        )}
        {/* Which 30 seconds (owner 2026-10-21: "give the user the option to choose which part of the song ... the same as
            they do it for facebook insta"); the whole song needs no choosing. */}
        {tool === 'canvas' && picked.song?.[0] && songTier !== 'full' && (
          <SongPartPicker
            file={picked.song[0]}
            start={choices.songstart ?? ''}
            onStart={(s) => setChoices((prev) => ({ ...prev, songstart: s }))}
            label={k('songPart.label')}
            hint={k('songPart.hint')}
            playLabel={k('songPart.play')}
            pauseLabel={k('songPart.pause')}
            onTime={setLyricTime}
            onSong={setSongSeconds}
          />
        )}
        {shownFields.filter((f) => f.type === 'text').map((f) => <FieldInput key={f.name} tool={tool} field={f} short />)}
        {/* Lyrics on screen (owner 2026-10-22): pasted, shown on the picture now, timed to the vocals in the video. */}
        {hasSong && shownFields.filter((f) => f.type === 'lyrics').map((f) => (
          <label key={f.name} className="mt-4 block" data-testid="qt-lyrics">
            <span className="mb-1.5 block text-[14px] font-medium text-muted-foreground">
              {k(`fields.${f.name}.label`)}
              <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[12px] font-semibold text-primary" data-testid="qt-lyrics-price">{k(`fields.${f.name}.addon`, { price: lyricsPrice })}</span>
            </span>
            <textarea value={choices[f.name] ?? ''} onChange={(e) => setChoices((prev) => ({ ...prev, [f.name]: e.target.value }))} rows={6} maxLength={f.max}
              placeholder={k(`fields.${f.name}.placeholder`)}
              className="w-full rounded-xl border border-white/15 bg-[hsl(220_18%_7%)] px-3.5 py-3 text-[15px] leading-snug text-foreground outline-none focus:border-primary/70" />
            <span className="mt-1 block text-[12px] leading-snug text-muted-foreground">{k(`fields.${f.name}.hint`)}</span>
          </label>
        ))}
        {tool === 'canvas' && (
          <p className="mt-3 rounded-xl bg-white/[0.04] px-3 py-2 text-[13px] leading-snug text-foreground" data-testid="qt-song-price">
            {k(hasSong ? 'studio.priceSoFar' : 'studio.priceNoSong', { price: chosenPrice })}
          </p>
        )}
      </>
    ),
    finish: (
      <>
        {/* Fine-tune: glow, amount, sparkle, fabric, grain, black and white, always open at the top of the last step
            (owner 2026-10-24: "shouldnt be behind a button ... the user shouldnt have to look for them or possibly miss
            them"); a slider opens right under the tiles, never over the picture ("its not blocking screen"). */}
        {pickedKind === 'image' && rangeFields.length + lookSwitches.length > 0 && (
          <div className="mb-4 rounded-2xl bg-white/[0.04]" data-testid="qt-finetune">
            <p className="flex min-h-[46px] items-center gap-2 px-3.5 text-[14.5px] font-semibold text-foreground">
              <SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden />{k('studio.fineTune')}
            </p>
            <div className="grid grid-cols-3 gap-x-2 gap-y-5 px-2 pb-4 pt-1">
                {rangeFields.map((f) => (
                  <AdjustTile key={f.name} icon={ADJUST_ICONS[f.name] || SlidersHorizontal} label={t(`qt.tools.${tool}.fields.${f.name}.short`)} value={choices[f.name] ?? f.default ?? '50'}
                    on={slider === f.name} onClick={() => setSlider(slider === f.name ? null : f.name)} testid={`qt-adjust-${f.name}`} />
                ))}
                {lookSwitches.map((f) => (
                  <AdjustTile key={f.name} icon={ADJUST_ICONS[f.name] || SlidersHorizontal} label={t(`qt.tools.${tool}.fields.${f.name}.short`)} value={k(isOn(f) ? 'studio.on' : 'studio.off')}
                    on={isOn(f)} onClick={() => setOn(f, !isOn(f))} testid={`qt-switch-${f.name}`}>
                    <input type="checkbox" name={f.name} checked={isOn(f)} readOnly hidden />
                  </AdjustTile>
                ))}
            </div>
            {slider && rangeFields.some((f) => f.name === slider) && (
              <div className="mx-2 mb-3 rounded-xl bg-black/40 px-3 pb-2 pt-3" data-testid="qt-float-slider">
                {rangeFields.filter((f) => f.name === slider).map((f) => (
                  <RangeField key={f.name} tool={tool} field={f} value={choices[f.name] ?? f.default ?? '50'} onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))} />
                ))}
                <div className="flex justify-end">
                  <button type="button" onClick={() => setSlider(null)} className="min-h-[36px] rounded-full bg-foreground px-4 text-[13px] font-bold text-background">{k('studio.done')}</button>
                </div>
              </div>
            )}
          </div>
        )}
        {shownFields.filter((f) => f.type === 'choice' && f.name !== 'subject' && !(tool === 'canvas' && f.name === 'length')).map((f) => (
          <ChoiceField key={f.name} tool={tool} field={f} value={choices[f.name] ?? f.default ?? f.options?.[0] ?? ''}
            onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))} prices={{ clip: clipPrice, full: fullPrice, pack: packPrice, packSave }} />
        ))}
        {tool === 'canvas' && (
          <div className="mb-3 rounded-2xl bg-white/[0.04] px-3.5 py-3 text-[13.5px]" data-testid="qt-price-lines">
            {priceLines.map(([label, cents]) => (
              <p key={label} className="flex justify-between gap-3 py-0.5 text-foreground/85"><span>{label}</span><span className="tabular-nums">{cents > 0 && priceLines[0][0] !== label ? '+' : ''}{money(cents)}</span></p>
            ))}
            <p className="mt-1.5 flex justify-between gap-3 border-t border-white/10 pt-1.5 text-[15px] font-bold text-foreground">
              <span>{k('studio.price.total')}</span>
              <span className="tabular-nums">{chosenPrice}{payByCredits && <span className="ml-1.5 text-[12.5px] font-semibold text-muted-foreground">({k('studio.price.credits', { credits: creditCost.toLocaleString() })})</span>}</span>
            </p>
            {songSteps && (
              <button type="button" onClick={() => goStep(steps.indexOf('song'))} className="mt-1.5 text-[12.5px] font-semibold text-primary underline-offset-2 hover:underline" data-testid="qt-price-change">
                {k('studio.price.change')}
              </button>
            )}
          </div>
        )}
        {/* The Release pack (owner 2026-10-22): three Full packs as credits, for an EP or three singles. */}
        {tool === 'canvas' && hasSong && (
          <Link to="/pricing#credits" className="mt-2 block rounded-xl bg-white/[0.04] px-3 py-2 text-[12.5px] leading-snug text-muted-foreground hover:text-foreground" data-testid="qt-release-pack">
            {k('studio.releasePack')}
          </Link>
        )}
        {shownFields.filter((f) => f.type === 'url').map((f) => <FieldInput key={f.name} tool={tool} field={f} short />)}
        {markSwitch && <ToggleRow icon={Stamp} name={markSwitch.name} label={t(`qt.tools.${tool}.fields.${markSwitch.name}.short`)} checked={isOn(markSwitch)} onChange={(on) => setOn(markSwitch, on)} />}
        {shownFields.some((f) => f.type === 'release') && songTier !== 'pack' && (
          <p className="mt-4 rounded-xl bg-white/[0.04] px-3 py-2 text-[12.5px] leading-snug text-muted-foreground" data-testid="qt-release-in-pack">{k('studio.releaseInPack')}</p>
        )}
        {shownFields.some((f) => f.type === 'release') && songTier === 'pack' && (
          <ReleasePicker
            value={choices.release ?? ''}
            onChange={(v) => setChoices((prev) => ({ ...prev, release: v }))}
            day={choices.releaseday || 'fri'}
            onDay={(v) => setChoices((prev) => ({ ...prev, releaseday: v }))}
            presave={choices.presave ?? ''}
            onPresave={(v) => setChoices((prev) => ({ ...prev, presave: v }))}
            lang={textLang}
            onLang={(v) => setChoices((prev) => ({ ...prev, textlang: v }))}
            songname={songName}
            picture={pictureUrl}
            shown={releaseShown}
            onShow={setReleaseShown}
          />
        )}
        <p className="mt-4 text-[13px] leading-snug text-muted-foreground" data-testid="qt-includes">
          {k(pictureOnly ? (hasSong ? 'studio.gets.pictureSong' : 'studio.gets.picture') : songTier === 'pack' ? 'studio.gets.pack' : wholeSong ? 'studio.gets.full' : hasSong ? 'studio.gets.clip' : 'studio.gets.loop')}
          {!pictureOnly && platformCount(sentPlatforms) > 0 && ` ${k('studio.gets.platforms')}`}
          {lyricLines.length > 0 && ` ${k('studio.gets.lyrics')}`}
          {releasePicked.length > 0 && ` ${k('studio.gets.releases', { count: releasePicked.length })}`}
        </p>
        <button
          type="submit"
          disabled={progress !== null}
          data-testid="qt-go"
          className="mt-3 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-5 text-[16.5px] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7)] transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          {progress !== null ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : <Sparkles className="h-5 w-5" aria-hidden />}
          {payByCredits ? t('qt.tools.canvas.goCredits', { credits: creditCost.toLocaleString(), price: chosenPrice }) : t('qt.tools.canvas.go', { price: chosenPrice })}
        </button>
        <p className="mt-2 text-center text-[12px] leading-snug text-muted-foreground">
          {payByCredits
            ? t(wholeSong ? 'qt.tools.canvas.payFirstCreditsFull' : 'qt.tools.canvas.payFirstCredits', { credits: creditCost.toLocaleString(), balance: balance.toLocaleString() })
            : t(wholeSong ? 'qt.tools.canvas.payFirstFull' : 'qt.tools.canvas.payFirst', { price: chosenPrice })}
        </p>
        {progress !== null && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10" role="status" aria-label={t('qt.uploading')}>
            <i className="block h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        )}
        {error && <p className="mt-3 text-[14px] text-destructive" role="alert">{error}</p>}
        <CheckoutStrip methods="card" />
      </>
    ),
  };
  // The promise, what you get and the share row: in the header, or under the upload on the Living Cover (design review #1:
  // with no photo yet the first screen is the title and one big upload button).
  const heroDetails = (
    <>
      <p className="mt-3 max-w-2xl text-pretty text-[15px] leading-relaxed text-muted-foreground sm:text-[1.05rem]">
        {k('lead', { price: price ?? '…' })}
      </p>
      {tool === 'canvas' && (
        <ul className="mt-4 grid max-w-2xl gap-2 text-[14.5px] leading-snug sm:text-[15px]" data-testid="qt-you-get">
          {(['getLoop', 'getSong', 'getPromote', 'getLink', 'getBass'] as const).map((key) => (
            <li key={key} className="flex gap-2.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
              <span>{t(`qt.tools.canvas.${key}`, { price, fullPrice })}</span>
            </li>
          ))}
        </ul>
      )}
      <ShareToolBar path={spec.path} text={tool === 'canvas' ? k('h1b') : `${k('h1a')} ${k('h1b')}`} className="mt-5" />
    </>
  );
  const editor = studio && stage === 'upload' && firstPicked ? (
    <div
      className={cn('fixed inset-0 z-[45] bg-[#07080b] pt-[4rem] text-foreground',
        editorShown ? 'flex flex-col lg:grid lg:grid-cols-[88px_minmax(340px,460px)_minmax(0,1fr)] lg:grid-rows-[60px_minmax(0,1fr)]' : 'hidden')}
      data-testid="qt-editor"
      // Where a floating slider can sit without covering the open sheet (phone: above it).
      style={{ ['--qt-float-bottom' as string]: phone && openPane && !slider && !placing && !placingBack ? 'calc(46dvh + 4.75rem)' : phone ? '4.75rem' : '1.5rem' }}
    >
      {bf && <input type="hidden" name={bf.name} value={backdropOn ? choices[bf.name] : ''} />}
      {of && <input type="hidden" name={of.name} value={floatPicked.join(',') || 'none'} />}
      {shownFields.some((f) => f.type === 'opacity') && <input type="hidden" name="opacity" value={choices.opacity ?? ''} />}
      {/* Top bar: close, undo, redo, compare; Export on the right. */}
      <div className="flex h-14 shrink-0 items-center gap-0.5 px-2 lg:col-start-3 lg:row-start-1 lg:h-auto lg:px-5">
        <EditorIconButton label={k('studio.close')} onClick={() => setEditorOpen(false)} testid="qt-editor-close"><X className="h-5 w-5" /></EditorIconButton>
        <span aria-hidden className="mx-1 h-6 w-px bg-white/10" />
        <EditorIconButton label={k('studio.undo')} onClick={undo} disabled={!hist.current.past.length} testid="qt-undo"><Undo2 className="h-5 w-5" /></EditorIconButton>
        <EditorIconButton label={k('studio.redo')} onClick={redo} disabled={!hist.current.future.length} testid="qt-redo"><Redo2 className="h-5 w-5" /></EditorIconButton>
        {pictureUrl && (
          <EditorIconButton label={k('studio.compare')} onPointerDown={() => setCompare(true)} onPointerUp={endCompare} onPointerLeave={endCompare} testid="qt-compare"><Columns2 className="h-5 w-5" /></EditorIconButton>
        )}
        {/* The song over the picture from the part picked, in any step: with the bass shake on, the picture shakes to it. */}
        {songUrl && (
          <>
            <audio
              ref={stageAudio}
              src={songUrl}
              preload="auto"
              hidden
              onTimeUpdate={(e) => {
                const a = e.currentTarget;
                if (a.currentTime >= songFrom + songLen) a.currentTime = songFrom;
                setLyricTime(Math.max(0, a.currentTime - songFrom));
              }}
              onEnded={(e) => { e.currentTarget.currentTime = songFrom; void e.currentTarget.play().catch(() => undefined); }}
            />
            <button type="button" onClick={toggleStageSong} aria-pressed={stagePlaying} data-testid="qt-stage-song"
              className={cn('ml-1 inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold', stagePlaying ? 'bg-white text-[#10131a]' : 'bg-white/[0.08] text-foreground')}>
              {stagePlaying ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
              <span className="hidden sm:inline">{k(stagePlaying ? 'studio.songStop' : bassOn ? 'studio.songPlayBass' : 'studio.songPlay')}</span>
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => { setSlider(null); setPane(openPane === 'finish' && phone ? null : 'finish'); }}
          aria-pressed={openPane === 'finish'}
          data-testid="qt-export"
          className="ml-auto inline-flex min-h-[40px] items-center gap-1.5 rounded-full bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-4 text-[14px] font-bold text-[#10131a] shadow-[0_8px_24px_-10px_rgba(244,190,82,0.8)] active:scale-[0.97]"
        >
          {exportLabel}
        </button>
      </div>
      {/* The stage: the picture, whole, as big as fits. Press and hold it to see the original. */}
      {/* Placing an overlay: the picture sits above its slider bar, so the bar never covers its corners. */}
      <div className={cn('relative flex min-h-0 flex-1 items-center justify-center px-3 pb-3 lg:col-start-3 lg:row-start-2 lg:px-8 lg:pb-8', swiping && !placing && 'pb-8 lg:pb-12', (placing || placingBack) && 'pb-[6.75rem] lg:pb-32')}>
        {/* Placing a layer: what to do, between the picture and its slider bar, never on the picture (owner 2026-10-27). */}
        {(placing || placingBack) && (
          <p className="pointer-events-none fixed inset-x-3 bottom-[calc(var(--qt-float-bottom,4.75rem)+env(safe-area-inset-bottom)+3.6rem)] z-50 mx-auto max-w-md text-center text-[12.5px] font-semibold leading-snug text-white/85 lg:absolute lg:inset-x-8 lg:bottom-[5.25rem] lg:z-10" data-testid="qt-overlay-hint">
            <b className="text-primary">{placing ? overlayName(placing) : placingBack ? (LIB_BACKDROPS[placingBack] ? libName(LIB_BACKDROPS[placingBack], i18n.language || 'en') : placingBack).replace(/\s+\d+$/, '') : ''}</b> · {k(placing ? 'studio.placeHintTurn' : 'studio.placeHint')}
          </p>
        )}
        <div
          className={cn('relative max-w-full touch-none select-none [-webkit-touch-callout:none]', mainSize.w > mainSize.h ? 'w-full max-h-full' : 'h-full')}
          style={{ aspectRatio: pickedKind === 'image' ? `${mainSize.w} / ${mainSize.h}` : '9 / 16' }}
          onPointerDown={(e) => {
            if (painting.kind && openPane === 'move') return;
            swipeX.current = e.clientX;
            downAt.current = Date.now();
            if (pictureUrl) startCompare();
          }}
          onPointerMove={(e) => {
            if (swipeX.current !== null && Math.abs(e.clientX - swipeX.current) > 12) window.clearTimeout(compareTimer.current);
          }}
          onPointerUp={(e) => {
            const dx = swipeX.current === null ? 0 : e.clientX - swipeX.current;
            swipeX.current = null;
            endCompare();
            if (swiping && Math.abs(dx) > 40) swipeTo(dx < 0 ? 1 : -1);
            // A quick tap on the picture picks the first overlay on it to move and resize.
            else if (swiping && !placing && floatPicked.length && Math.abs(dx) < 8 && Date.now() - downAt.current < 300) setOverlayFocus(floatPicked[0]);
          }}
          onPointerLeave={() => {
            swipeX.current = null;
            endCompare();
          }}
          onPointerCancel={endCompare}
          onContextMenu={(e) => e.preventDefault()}
          data-testid="qt-editor-preview"
        >
          {pickedKind === 'video' && firstPicked ? <PickedClip file={firstPicked} /> : livingCover('h-full w-full rounded-2xl')}
          {/* The words the videos will carry: a release version tapped in the Export step, else the lyrics (never on the Spotify loop). */}
          {!compare && openPane === 'finish' && releaseShown && releasePicked.includes(releaseShown) ? (
            <ReleaseCard words={releaseWords(releaseShown, { lang: textLang, day: choices.releaseday || 'fri', songname: songName, presave: choices.presave ?? '' })} className="rounded-2xl" />
          ) : !compare && lyricLines.length ? (
            <LyricsOverlay lines={lyricLines} time={lyricTime} len={30} ratio={mainSize.h / mainSize.w}
              top={Boolean(depth?.depth && depth.box && depth.box[3] > 0.7 && depth.box[1] < 0.83 && depth.box[1] >= 0.3)} />
          ) : null}
          {/* What was found, shown on the picture while on its step; tapping it there picks the subject (TapLayer). */}
          {/* SELECTING (owner 2026-10-27): the picture still and plain (no motion, glow, overlays or backdrop), what is
              selected tinted over it, the brush's strokes on top; the living preview comes back with "See the result". */}
          {!compare && selecting && paintMap && (
            <div className="absolute inset-0 overflow-hidden rounded-2xl bg-black" data-testid="qt-crop-select-view">
              {/* What is removed shows as gone: a checkerboard with the picture faint over it (still tappable to add back);
                  what stays is the picture itself, and each stroke shows the moment it is made (CutLayer, owner 2026-10-28:
                  no colour painted over them, no waiting to see it). */}
              {/* Zoomed in close to clean the edges (owner 2026-10-28), on a phone or a computer. */}
              <ZoomBox hand={cutHand} onHand={setCutHand} onPinch={setPinching}
                labels={{ zoomIn: k('motion.zoom.in'), zoomOut: k('motion.zoom.out'), fit: k('motion.zoom.fit'), hand: k('motion.zoom.hand') }}>
                <div aria-hidden className="pointer-events-none absolute inset-0 [background-color:#2a2d35] [background-image:conic-gradient(#3a3e48_25%,transparent_0_50%,#3a3e48_0_75%,transparent_0)] [background-size:22px_22px]" />
                {/* The backdrop stays in place behind the cut while it is fixed (owner 2026-11-01). */}
                {backdropOn && (
                  <div aria-hidden className="pointer-events-none absolute inset-0" data-testid="qt-crop-backdrop">
                    <BackdropStack layers={coverBackdrops} plate={stageDepth?.plate} frame={mainSize} filter="none" />
                  </div>
                )}
                {/* The removed part, faint, to bring back: with a backdrop, only while the brush is out. */}
                {(mainStill || pictureUrl) && (!backdropOn || brush.mode) && (
                  <img src={mainStill || pictureUrl || ''} alt="" draggable={false} className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover opacity-25" />
                )}
                {/* The picture itself, full size, faint where it was removed: sharp when zoomed in close. */}
                {pictureUrl && paintMap && (!backdropOn || brush.mode) && (() => {
                  const [x0, y0] = paintMap.toFrame(0, 0);
                  const [x1, y1] = paintMap.toFrame(1, 1);
                  return <img src={pictureUrl} alt="" draggable={false} className="pointer-events-none absolute select-none opacity-25"
                    style={{ left: `${x0 * 100}%`, top: `${y0 * 100}%`, width: `${(x1 - x0) * 100}%`, height: `${(y1 - y0) * 100}%` }} />;
                })()}
                {selectWhich && (
                  <CutLayer map={paintMap} picture={mainStill || pictureUrl} mask={keptMask} none={selectWhich === 'keep' ? 'empty' : 'full'}
                    which={selectWhich} pending={cutPending} active={brush.mode === selectWhich && !cutHand} adds={brushAdds} onStroke={addStroke}
                    brushR={CUT_SIZES[cutSize]} blocked={pinching} original={pictureUrl} />
                )}
              </ZoomBox>
              {depthBusy && (
                <span className="pointer-events-none absolute left-2.5 top-2.5 flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-[12px] font-semibold text-white" data-testid="qt-crop-busy">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{k('motion.brush.finding')}
                </span>
              )}
            </div>
          )}
          {/* Painting over the picture (Make it move): the painted areas tinted, the finger paints the kind picked. */}
          {!compare && openPane === 'move' && paintMap && (choices.paint || painting.kind) && (
            <PaintLayer paint={choices.paint ?? ''} onPaint={(v) => setChoices((prev) => ({ ...prev, paint: v }))} painting={painting} map={paintMap} />
          )}
          {motionForCut && openPane === 'move' && motionShown && !motionBusy && !compare && (
            <span className="pointer-events-none absolute inset-x-2.5 bottom-2.5 mx-auto w-fit max-w-full rounded-full bg-black/65 px-2.5 py-1 text-center text-[12px] font-semibold text-white" data-testid="qt-motion-small">{k('motion.smallNote')}</span>
          )}
          {motionBusy && !compare && (
            <span className="pointer-events-none absolute left-2.5 top-2.5 flex items-center gap-1.5 rounded-full bg-black/65 px-2.5 py-1 text-[12px] font-semibold text-white" data-testid="qt-motion-busy">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{k('motion.making')}
            </span>
          )}
          {compare && pictureUrl && (
            <>
              <img src={pictureUrl} alt="" className="pointer-events-none absolute inset-0 h-full w-full rounded-2xl object-cover" />
              <span className="pointer-events-none absolute left-2.5 top-2.5 rounded-full bg-black/65 px-2.5 py-1 text-[12px] font-semibold text-white">{k('studio.original')}</span>
            </>
          )}
          {/* Moving and resizing the picked overlay on the picture (InShot's overlays). */}
          {!compare && placing && (
            <OverlayPlacer
              place={coverSpot[placing] ?? null}
              frame={pickedKind === 'image' ? mainSize : { w: 1080, h: 1920 }}
              label={overlayName(placing)}
              hint={k('studio.placeHintTurn')}
              turn
              turnLabel={k('studio.turn')}
              onChange={(p) => setChoices((prev) => {
                const next = { ...parseSpot(prev.spot ?? ''), [placing]: p };
                return { ...prev, spot: formatSpot(next) };
              })}
            />
          )}
          {!compare && placingBack && (
            <OverlayPlacer
              place={backSpot[placingBack] ?? null}
              frame={mainSize.w && mainSize.h ? mainSize : { w: 1080, h: 1920 }}
              label={(LIB_BACKDROPS[placingBack] ? libName(LIB_BACKDROPS[placingBack], i18n.language || 'en') : placingBack).replace(/\s+\d+$/, '')}
              hint={k('studio.placeHintTurn')}
              // Turned too, like the overlays (owner 2026-10-28: "enlarging it roatating it and moving it").
              turn
              turnLabel={k('studio.turn')}
              onChange={(p) => setChoices((prev) => ({ ...prev, bspot: formatSpot({ ...parseSpot(prev.bspot ?? ''), [placingBack]: p }) }))}
            />
          )}
          {/* Each overlay on the picture can be picked here to move and resize it. */}
          {!compare && swiping && !placing && floatPicked.length > 0 && (
            <div className="absolute inset-x-2 top-2 z-10 flex flex-wrap justify-center gap-1.5" data-testid="qt-place-pick">
              {floatPicked.map((id) => (
                <button key={id} type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => setOverlayFocus(id)} aria-label={k('studio.placeOf', { name: overlayName(id) })}
                  className="inline-flex min-h-[32px] max-w-[9rem] items-center gap-1.5 whitespace-nowrap rounded-full bg-black/65 px-3 text-[12.5px] font-semibold text-white backdrop-blur hover:bg-black/80" data-testid={`qt-place-${id}`}>
                  <Move className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden /><span className="truncate">{overlayName(id)}</span>
                </button>
              ))}
            </div>
          )}
          {swiping && !placing && (
            <>
              <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => swipeTo(-1)} aria-label={k('studio.prevOverlay')} data-testid="qt-swipe-prev" className="absolute left-1.5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/70">
                <ChevronLeft className="h-6 w-6" aria-hidden />
              </button>
              <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => swipeTo(1)} aria-label={k('studio.nextOverlay')} data-testid="qt-swipe-next" className="absolute right-1.5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/70">
                <ChevronRight className="h-6 w-6" aria-hidden />
              </button>
              {/* What to do, under the picture, never on it (owner 2026-10-27: instructions "placed on the image blocking out
                  the view while your trying to edit it"); a swipe's overlay name shows there too. */}
              <span className="pointer-events-none absolute inset-x-0 top-[calc(100%+0.35rem)] mx-auto w-fit max-w-full truncate px-2 text-center text-[12.5px] font-semibold text-white/80" data-testid="qt-swipe-tip">
                {swipeTip ?? k('studio.swipeHint')}
              </span>
            </>
          )}
        </div>
      </div>
      {/* The brush on a phone (owner 2026-10-27): the sheet tucks away so the picture is big, the brush's tools float under it. */}
      {phone && brush.mode && (
        <div className="fixed inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md flex-wrap items-center justify-center gap-1.5 rounded-2xl bg-black/80 p-2 ring-1 ring-white/10 backdrop-blur" data-testid="qt-crop-bar">
          <button type="button" aria-pressed={brushAdds} onClick={() => { setCutHand(false); setBrush((b) => ({ ...b, erase: b.mode === 'replace' })); }} className={cn('flex min-h-[40px] items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold', brushAdds ? 'bg-white text-[#10131a]' : 'bg-white/[0.1] text-white')}>
            <Brush className="h-4 w-4" aria-hidden />{k('motion.brush.add')}
          </button>
          <button type="button" aria-pressed={!brushAdds} onClick={() => { setCutHand(false); setBrush((b) => ({ ...b, erase: b.mode !== 'replace' })); }} className={cn('flex min-h-[40px] items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold', !brushAdds ? 'bg-white text-[#10131a]' : 'bg-white/[0.1] text-white')}>
            <Eraser className="h-4 w-4" aria-hidden />{k('motion.brush.cut')}
          </button>
          <CutSizes size={cutSize} onSize={(z) => { setCutHand(false); setCutSize(z); }} dark />
          <button type="button" onClick={undo} disabled={!undoCount} className="flex min-h-[40px] items-center gap-1.5 rounded-full bg-white/[0.1] px-3 text-[13px] font-semibold text-white disabled:opacity-40">
            <Undo2 className="h-4 w-4" aria-hidden />{k('motion.brush.undo')}
          </button>
          <button type="button" onClick={brushCancel} className="min-h-[40px] rounded-full bg-white/[0.06] px-3 text-[13px] font-semibold text-white/80">{k('motion.brush.cancel')}</button>
          <button type="button" onClick={brushDone} className="flex min-h-[40px] items-center gap-1.5 rounded-full bg-primary px-4 text-[13px] font-bold text-primary-foreground">
            <Check className="h-4 w-4" aria-hidden />{k('motion.brush.done')}
          </button>
        </div>
      )}
      {/* The open tool: a sheet over the bottom on a phone (drag its handle down to close), a panel beside the picture on a laptop. */}
      <div
        className={cn('relative shrink-0 overflow-hidden rounded-t-[28px] bg-[#15171d] transition-[height] duration-300 ease-out lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:h-auto lg:rounded-none lg:bg-[#0f1116]',
          // A slider being moved, or an overlay being placed, tucks the phone's sheet away (Instagram, InShot): the picture is big.
          openPane && !(phone && (slider || placing || brush.mode)) ? 'h-[46dvh]' : 'h-0')}
        data-testid="qt-sheet"
      >
        <div className="flex h-full flex-col">
          <div
            className="shrink-0 touch-none px-4 pb-1 pt-2 lg:px-5 lg:pt-4"
            onPointerDown={(e) => { grabY.current = e.clientY; }}
            onPointerUp={(e) => {
              if (grabY.current !== null && e.clientY - grabY.current > 40) setPane(null);
              grabY.current = null;
            }}
          >
            <span aria-hidden className="mx-auto block h-1.5 w-10 rounded-full bg-white/20 lg:hidden" />
            {stepAt >= 0 && (
              <div className="mt-2 flex gap-1 lg:mt-0" aria-hidden>
                {steps.map((id, i) => <i key={id} className={cn('h-1 flex-1 rounded-full', i <= stepAt ? 'bg-primary' : 'bg-white/10')} />)}
              </div>
            )}
            <div className="mt-2 flex min-h-[34px] items-center justify-between gap-3">
              <h2 className="flex min-w-0 items-baseline gap-2 text-[17px] font-bold leading-tight">
                {shownPane ? k(`studio.panes.${shownPane}`) : ''}
                {stepAt >= 0 && <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-primary" data-testid="qt-step-of">{k('studio.stepOf', { n: stepAt + 1, total: steps.length })}</span>}
              </h2>
              <button type="button" onClick={() => { setSlider(null); setPane(null); }} aria-label={k('studio.done')} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-foreground lg:hidden">
                <ChevronDown className="h-5 w-5" aria-hidden />
              </button>
            </div>
            {shownPane && <p className="mt-1 text-[13.5px] leading-snug text-foreground/80" data-testid="qt-step-guide">{k(`studio.guide.${shownPane}`)}</p>}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-2 lg:px-5">
            {(Object.keys(paneBody) as EditorPane[]).map((id) => (
              <div key={id} className={cn('min-w-0', shownPane !== id && 'hidden')} data-testid={`qt-pane-${id}`}>{paneBody[id]}</div>
            ))}
          </div>
          {stepAt >= 0 && (
            <div className="flex shrink-0 gap-2 border-t border-white/[0.06] px-4 py-2.5 lg:px-5 lg:py-3">
              {stepAt > 0 && (
                <button type="button" onClick={() => goStep(stepAt - 1)} data-testid="qt-step-back" className="min-h-[48px] rounded-2xl bg-white/[0.07] px-4 text-[15px] font-semibold text-foreground hover:bg-white/[0.12]">
                  {k('studio.back')}
                </button>
              )}
              {stepAt < steps.length - 1 && (
                <button
                  type="button"
                  onClick={() => goStep(stepAt + 1)}
                  data-testid="qt-step-next"
                  className={cn('flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-2xl px-4 text-[16px] font-bold transition-transform active:scale-[0.98]',
                    steps[stepAt + 1] === 'finish' ? 'bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[#10131a]' : 'bg-white text-[#10131a]')}
                >
                  {k('studio.next', { name: k(`studio.panes.${steps[stepAt + 1]}`) })}
                  <ChevronRight className="h-5 w-5" aria-hidden />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* The toolbar: icon + one word, along the bottom of a phone, down the left of a laptop. */}
      <nav
        aria-label={k('studio.tools')}
        className="grid shrink-0 auto-cols-fr grid-flow-col border-t border-white/[0.06] bg-[#07080b] pb-[env(safe-area-inset-bottom)] lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:flex lg:flex-col lg:gap-1 lg:border-r lg:border-t-0 lg:px-2 lg:py-3"
      >
        {panes.map(([id, Icon]) => {
          const on = shownPane === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => { setSlider(null); setPane(on && phone ? null : id); }}
              aria-pressed={on}
              data-testid={`qt-tool-${id}`}
              className={cn('flex min-h-[62px] flex-col items-center justify-center gap-1 text-[11.5px] font-semibold transition-colors lg:min-h-[68px] lg:rounded-xl',
                on ? 'text-primary lg:bg-white/[0.07]' : 'text-muted-foreground hover:text-foreground')}
            >
              <span className="relative">
                <Icon className="h-[22px] w-[22px]" aria-hidden />
                {seen.includes(id) && !on && (
                  <span aria-hidden className="absolute -right-2 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[#10131a]"><Check className="h-3 w-3" strokeWidth={3} /></span>
                )}
              </span>
              {k(`studio.panes.${id}`)}
            </button>
          );
        })}
      </nav>
    </div>
  ) : null;

  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      <Navbar />
      <main className="pt-20 sm:pt-24">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 xl:px-4">
          {/* While the example phones show, their column gets half the width (owner 2026-10-20: "make these a bit bigger"). */}
          <div className={cn('grid gap-8 lg:items-start', reelOn ? 'lg:grid-cols-2' : 'lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]')}>
            <div>
              <header>
                <p className="eyebrow" translate="no">{t('qt.eyebrow')}</p>
                {/* Owner 2026-10-19: "make the word spotify in green and above the living cover text". */}
                {tool === 'canvas' && <CanvasAppsLine className="mt-3" />}
                <h1 className="mt-3 text-balance font-display text-[30px] font-bold leading-[1.08] tracking-[-0.02em] sm:text-[42px]">
                  {/* The Living Cover's title is its sentence alone (owner 2026-10-20: "you can ditch the living cover the beginning"). */}
                  {tool !== 'canvas' && <>{k('h1a')} </>}<span className="gradient-text">{k('h1b')}</span>
                </h1>
                {tool === 'canvas' && <CanvasByline className="mt-2" />}
                {!studio && heroDetails}
              </header>

              {/* THE ONE ACTION: upload (free) → preview (free) → pay to download. */}
              <section aria-live="polite" className="mt-6">
                {stage === 'upload' && (
                  <form ref={formRef} onSubmit={onSubmit} onChange={() => setFormTick((n) => n + 1)} data-testid="qt-form" className="rounded-3xl border border-white/10 bg-[linear-gradient(180deg,hsl(var(--card)),hsl(219_22%_9%))] p-4 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.7)] sm:p-6">
                    {studio ? (
                      // One big upload button; the picture opens the editor (and the way back into it once closed).
                      <>
                        {editor}
                        {!editorShown && uploads.filter((u) => !u.optional).map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} studio />)}
                        {/* The song on the same screen (owner 2026-10-23: "when they choose their image to upload they upload their song at the same time"). */}
                        {!editorShown && !firstPicked && uploads.filter((u) => u.optional).map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} studio />)}
                        {firstPicked && !editorShown && (
                          <button type="button" onClick={() => setEditorOpen(true)} data-testid="qt-editor-open" className="mt-3 flex min-h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-5 text-[16.5px] font-bold text-[#10131a]">
                            <SlidersHorizontal className="h-5 w-5" aria-hidden />{k('studio.open')}
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        {!spec.formTop && uploads.map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} />)}
                        {shownFields.filter((f) => !f.example && f.type !== 'choice' && f.type !== 'multi' && f.type !== 'backdrop' && f.type !== 'range' && f.type !== 'opacity' && f.type !== 'songstart' && f.type !== 'formats' && f.type !== 'lyrics' && f.type !== 'release' && f.type !== 'state' && !(f.when && f.type === 'checkbox')).map((f) => (
                          <FieldInput key={f.name} tool={tool} field={f} voices={config?.voices} voiceOff={voiceOff} />
                        ))}
                      </>
                    )}
                    {shownFields.some((f) => f.example) && (
                      // Effects you can combine, each with a moving example (owner 2026-10-16).
                      <fieldset className="mt-5" data-testid="qt-effects">
                        <legend className="mb-2 text-[14px] font-medium text-muted-foreground">{k('effectsTitle')}</legend>
                        <div className="grid grid-cols-3 gap-2 sm:gap-3">
                          {shownFields.filter((f) => f.example).map((f) => (
                            <label key={f.name} className="group cursor-pointer rounded-2xl border border-white/10 bg-white/[0.03] p-2 has-[:checked]:border-primary/60 has-[:checked]:bg-primary/10">
                              <video src={f.example} autoPlay muted loop playsInline preload="metadata" aria-hidden className="aspect-[9/16] w-full rounded-xl bg-black object-cover" />
                              <span className="mt-2 flex items-start gap-2">
                                <input type="checkbox" name={f.name} defaultChecked={f.checked} className="mt-0.5 h-5 w-5 shrink-0 accent-[hsl(var(--primary))]" />
                                <span>
                                  <span className="block text-[14px] font-semibold leading-tight text-foreground">{t(`qt.tools.${tool}.fields.${f.name}.label`)}</span>
                                  <span className="mt-0.5 block text-[12px] leading-snug text-muted-foreground">{t(`qt.tools.${tool}.fields.${f.name}.hint`)}</span>
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    )}
                    {/* What the picture is of (owner 2026-10-20: "A STEP THAT ASKS THE USER WHAT THE IMAGE IS OF"): it decides what moves. First, before the looks. */}
                    {!studio && shownFields.filter((f) => f.type === 'choice' && f.name === 'subject').map((f) => (
                      <ChoiceField
                        key={f.name}
                        tool={tool}
                        field={f}
                        value={choices[f.name] ?? f.default ?? ''}
                        onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))}
                        prices={{}}
                      />
                    ))}
                    {/* The library (owner 2026-10-21: "before any category the first categories they go into are backdrops and
                        than overlays... those need to be seperated"): one choice first, Backdrops or Overlays, then its own groups. */}
                    {(() => {
                      const bf = shownFields.find((f) => f.type === 'backdrop');
                      const of = shownFields.find((f) => f.type === 'multi');
                      if (studio || !bf || !of || !Object.keys(LIB_BACKDROPS).length) return null;
                      return (
                        <CoverLibrary
                          tool={tool}
                          backdropField={bf}
                          floatField={of}
                          backdrop={choices[bf.name] ?? ''}
                          float={choices[of.name] ?? of.default ?? ''}
                          opacity={choices.opacity ?? ''}
                          onBackdrop={(v) => setChoices((prev) => ({ ...prev, [bf.name]: v }))}
                          onFloat={(v) => setChoices((prev) => ({ ...prev, [of.name]: v }))}
                          onOpacity={(v) => setChoices((prev) => ({ ...prev, opacity: v }))}
                          subject={subjectSaid}
                          depth={depth}
                          depthBusy={depthBusy}
                          picked={Boolean(firstPicked)}
                          cut={cutOn}
                          onCut={setCut}
                          photo={pictureUrl}
                        />
                      );
                    })()}
                    {/* Alone (no backdrops in the library): each field as before. */}
                    {!(shownFields.some((f) => f.type === 'backdrop') && Object.keys(LIB_BACKDROPS).length) && shownFields.filter((f) => f.type === 'backdrop').map((f) => (
                      <BackdropField
                        key={f.name}
                        tool={tool}
                        field={f}
                        value={choices[f.name] ?? ''}
                        onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))}
                        subject={subjectSaid}
                        depth={depth}
                        depthBusy={depthBusy}
                        picked={Boolean(firstPicked)}
                        cut={cutOn}
                        onCut={setCut}
                      />
                    ))}
                    {/* Several at once: what floats in the air, each shown live on black (owner 2026-10-17: "the only thing that should be shown ... is the floating material itself"). */}
                    {!(shownFields.some((f) => f.type === 'backdrop') && Object.keys(LIB_BACKDROPS).length) && shownFields.filter((f) => f.type === 'multi').map((f) => (
                      <MultiField
                        key={f.name}
                        tool={tool}
                        field={f}
                        value={choices[f.name] ?? f.default ?? ''}
                        onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))}
                        opacity={choices.opacity ?? ''}
                        onOpacity={(v) => setChoices((prev) => ({ ...prev, opacity: v }))}
                      />
                    ))}
                    {!studio && (
                      <>
                        {shownFields.filter((f) => f.type === 'range').map((f) => (
                          <RangeField key={f.name} tool={tool} field={f} value={choices[f.name] ?? f.default ?? '50'} onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))} />
                        ))}
                        {shownFields.filter((f) => f.type === 'choice' && f.name !== 'subject').map((f) => (
                          <ChoiceField
                            key={f.name}
                            tool={tool}
                            field={f}
                            value={choices[f.name] ?? f.default ?? f.options?.[0] ?? ''}
                            onChange={(v) => setChoices((prev) => ({ ...prev, [f.name]: v }))}
                            prices={{ clip: clipPrice, full: fullPrice, pack: packPrice, packSave }}
                          />
                        ))}
                        {/* Plain switches under the choices. */}
                        {switchFields.map((f) => (
                          <FieldInput key={f.name} tool={tool} field={f} voices={config?.voices} voiceOff={voiceOff} />
                        ))}
                      </>
                    )}
                    {spec.formTop && uploads.map((u) => <DropZone key={u.field} tool={tool} upload={u} locked={lockUpload} files={picked[u.field]} onPick={onPick} voiceOff={voiceOff} />)}
                    {/* The Living Cover pays from the editor's Export sheet. */}
                    {!studio && (
                      <>
                        {hasForm && (
                          <button
                            type="submit"
                            disabled={progress !== null}
                            data-testid="qt-go"
                            className="mt-5 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-5 text-[17px] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7)] transition-transform active:scale-[0.98] disabled:opacity-60"
                          >
                            {payByCredits ? t('qt.makeAndPayCredits', { credits: creditCost.toLocaleString() }) : t('qt.makeAndPay', { price: chosenPrice })}
                          </button>
                        )}
                        <p className="mt-2 text-center text-[13px] text-muted-foreground">
                          {payByCredits ? t('qt.payFirstCredits', { credits: creditCost.toLocaleString(), balance: balance.toLocaleString() }) : t('qt.payFirst', { price: chosenPrice })}
                        </p>
                        {progress !== null && (
                          <div className="mt-4" role="status">
                            <div className="h-2 overflow-hidden rounded-full bg-white/10">
                              <i className="block h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
                            </div>
                            <p className="mt-2 text-[14px] text-muted-foreground">{t('qt.uploading')}</p>
                          </div>
                        )}
                        {error && <p className="mt-3 text-[15px] text-destructive" role="alert">{error}</p>}
                      </>
                    )}
                    {studio && !editorShown && error && <p className="mt-3 text-[15px] text-destructive" role="alert">{error}</p>}
                    {/* The Living Cover's upload button already says it. */}
                    {!studio && <p className="mt-3 text-center text-[12.5px] text-muted-foreground">{t('qt.rightsNote')}</p>}
                  </form>
                )}

                {stage === 'pay' && job && (
                  <div data-testid="qt-pay" className="rounded-3xl border border-white/10 bg-card p-6">
                    <p className="font-display text-xl font-bold">{t('qt.payTitle')}</p>
                    <p className="mt-2 text-muted-foreground">{t('qt.payLead')}</p>
                    <div className="mt-4 grid gap-2">
                      {payByCredits && (
                        <button type="button" disabled={buying} onClick={() => void payFor(job.id)} data-testid="qt-pay-credits" className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-5 text-[17px] font-bold text-[#10131a] disabled:opacity-70">
                          {buying ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : null}
                          {t('qt.payCredits', { credits: creditCost.toLocaleString() })}
                        </button>
                      )}
                      <button type="button" disabled={buying} onClick={() => void buy()} data-testid="qt-pay-card" className={cn('flex min-h-[52px] items-center justify-center gap-2 rounded-2xl px-5 text-[16px] font-bold disabled:opacity-70', payByCredits ? 'border border-white/15 text-foreground' : 'bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[#10131a]')}>
                        {t('qt.payCard', { price: payPrice })}
                      </button>
                    </div>
                    <CheckoutStrip methods="card" />
                    {error && <p className="mt-3 text-[15px] text-destructive" role="alert">{error}</p>}
                  </div>
                )}

                {stage === 'work' && (
                  <div data-testid="qt-working" className="rounded-3xl border border-white/10 bg-card p-6">
                    <p className="flex items-center gap-2 font-display text-xl font-bold">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden />
                      {t('qt.working')}
                    </p>
                    <p className="mt-2 text-muted-foreground">
                      {job?.status === 'queued' && job.position > 1 ? t('qt.inLine', { n: job.position }) : k('working')}
                    </p>
                    {tool === 'tapes' && job?.minutes ? <p className="mt-1 text-[14px] font-semibold text-foreground" data-testid="qt-tape-minutes">{t('qt.family.minutes', { n: job.minutes })}</p> : null}
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
                      <i className="block h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width] duration-500" style={{ width: `${Math.min(92, 12 + tickRef.current * 4)}%` }} />
                    </div>
                    <p className="mt-3 text-[13px] text-muted-foreground">{t('qt.keepLink')}</p>
                  </div>
                )}

                {stage === 'ready' && job && (
                  <div ref={readyRef} data-testid="qt-ready" className="scroll-mt-24 rounded-3xl border border-white/10 bg-card p-4 sm:p-6">
                    {/* The result shows only once it is paid for (owner 2026-10-16: "why
                        would we give a free preview when we have examples in front of
                        their face?"). Before that: the file is ready, pay to get it. */}
                    {paid && job.extra_files?.includes('song') ? (
                      // With a song, the version with the song plays first, with sound, so the beat can be heard (owner 2026-10-17).
                      <div data-testid="qt-song-player">
                        <p className="mb-2 text-[14px] font-semibold text-foreground">{t('qt.withSong', { seconds: Math.round(job.song_seconds || 30) })}</p>
                        <video key={`song-${job.marked ? 'm' : 'c'}`} src={downloadUrl(tool, job.id, { sessionId, file: 'song', inline: true, v: job.marked ? 'm' : 'c' })} controls playsInline className="mx-auto max-h-[60vh] w-full rounded-2xl border border-white/10 bg-black object-contain" />
                        {/* The Spotify Canvas file itself, shown too (owner 2026-10-19: "i dont see any 7.5 second loop result"). */}
                        {tool === 'canvas' && (
                          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="qt-canvas-loop">
                            <video key={`loop-${job.marked ? 'm' : 'c'}`} src={downloadUrl(tool, job.id, { sessionId, inline: true, v: job.marked ? 'm' : 'c' })} muted loop autoPlay playsInline className="w-20 shrink-0 rounded-xl bg-black object-cover" style={{ aspectRatio: job.size ? `${job.size.w} / ${job.size.h}` : '9 / 16' }} />
                            <div className="text-[13.5px] leading-snug">
                              <p className="font-semibold text-foreground">{t('qt.canvasLoopTitle')}</p>
                              <p className="mt-1 text-muted-foreground">{t('qt.canvasLoopHow')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : paid || opPaid ? (
                      job.preview_type === 'image' ? (
                        <img src={previewUrl(tool, job.id)} alt={t('qt.previewAlt')} className="mx-auto max-h-[60vh] w-full rounded-2xl border border-white/10 bg-black object-contain" />
                      ) : (
                        <video key={`loop-${job.marked ? 'm' : 'c'}`} src={`${previewUrl(tool, job.id)}?v=${job.marked ? 'm' : 'c'}`} controls playsInline loop className="mx-auto max-h-[60vh] w-full rounded-2xl border border-white/10 bg-black object-contain" />
                      )
                    ) : (
                      <p className="font-display text-xl font-bold">{t('qt.readyTitle')}</p>
                    )}
                    <p className="mt-3 text-[14px] text-muted-foreground">{paid || opPaid ? t('qt.paidNote') : t('qt.readyPay', { price: payPrice })}</p>

                    {paid ? (
                      <>
                      {/* The file sized to Spotify's 720-1080 px tall rule, first; the 1080x1920 loop is the one to try if Spotify refuses it (owner 2026-10-27). */}
                      {tool === 'canvas' && job.extra_files?.includes('spotify') && (
                        <>
                          <a
                            href={downloadUrl(tool, job.id, { sessionId, file: 'spotify' })}
                            data-testid="qt-download-spotify"
                            className="mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[17px] font-bold text-[#10131a]"
                          >
                            <Download className="h-5 w-5" aria-hidden />
                            {t('qt.canvasSpotifyDownload')}
                          </a>
                          <p className="mt-2 text-[13px] leading-snug text-muted-foreground" data-testid="qt-spotify-how">{t('qt.canvasSpotifyHow')}</p>
                        </>
                      )}
                      <a
                        href={downloadUrl(tool, job.id, { sessionId })}
                        data-testid="qt-download"
                        className={cn('mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl text-[17px] font-bold',
                          tool === 'canvas' && job.extra_files?.includes('spotify')
                            ? 'mt-3 border border-primary/50 bg-primary/10 px-3 text-center text-[16px] leading-tight text-foreground'
                            : 'bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[#10131a]')}
                      >
                        <Download className="h-5 w-5 shrink-0" aria-hidden />
                        {tool === 'canvas' ? (job.shape && job.shape !== '9x16' ? t('qt.canvasLoopDownloadShape', { ratio: job.size ? ratioLabel(job.shape as ShapeKey, job.size) : job.shape }) : job.extra_files?.includes('spotify') ? t('qt.canvasHdDownload') : t('qt.canvasLoopDownload')) : k('download')}
                      </a>
                      {job.extra_files?.includes('song') && (
                        <a
                          href={downloadUrl(tool, job.id, { sessionId, file: 'song' })}
                          data-testid="qt-download-song"
                          className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-primary/50 bg-primary/10 text-[16px] font-bold text-foreground"
                        >
                          <Download className="h-5 w-5" aria-hidden />
                          {t('qt.songDownload', { seconds: Math.round(job.song_seconds || 30) })}
                        </a>
                      )}
                      {/* The Full pack's whole song and the extra formats (owner 2026-10-22), each its own download. */}
                      {EXTRA_DOWNLOADS.some((x) => job.extra_files?.includes(x)) && (
                        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="qt-extra-downloads">
                          {EXTRA_DOWNLOADS.filter((x) => job.extra_files?.includes(x)).map((x) => (
                            <a key={x} href={downloadUrl(tool, job.id, { sessionId, file: x })} data-testid={`qt-download-${x}`}
                              className={cn('flex min-h-[48px] items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.05] px-2 text-center text-[13.5px] font-semibold leading-tight text-foreground hover:border-primary/60',
                                (x === 'whole' || x === 'whatsapp') && 'col-span-2 min-h-[52px] border-primary/50 bg-primary/10 text-[16px] font-bold')}>
                              <Download className="h-4 w-4 shrink-0" aria-hidden />{t(`qt.extraDownload.${x}`)}
                            </a>
                          ))}
                        </div>
                      )}
                      {job.lyrics && (
                        <p className="mt-2 text-[13px] leading-snug text-muted-foreground" data-testid="qt-lyrics-mode">{t(job.lyrics.mode === 'synced' ? 'qt.lyricsSynced' : 'qt.lyricsEven')}</p>
                      )}
                      {(job.releases || []).some((r) => job.extra_files?.includes(`release-${r}`)) && (
                        <div className="mt-4" data-testid="qt-releases">
                          <p className="text-[14px] font-semibold text-foreground">{t('qt.releasesTitle')}</p>
                          <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {(job.releases || []).filter((r) => job.extra_files?.includes(`release-${r}`)).map((r) => (
                              <a key={r} href={downloadUrl(tool, job.id, { sessionId, file: `release-${r}` })} data-testid={`qt-download-release-${r}`} className="group block">
                                <video src={downloadUrl(tool, job.id, { sessionId, file: `release-${r}`, inline: true, v: job.marked ? 'm' : 'c' })} muted loop autoPlay playsInline preload="metadata" className="w-full rounded-xl border border-white/10 bg-black object-cover" style={{ aspectRatio: job.size ? `${job.size.w} / ${job.size.h}` : '9 / 16' }} />
                                <span className="mt-1 flex items-center justify-center gap-1 text-[12.5px] font-semibold text-foreground group-hover:text-primary">
                                  <Download className="h-3.5 w-3.5" aria-hidden />{k(`fields.release.options.${r}`)}
                                </span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {job.can_unmark && (
                        <div className="mt-3" data-testid="qt-unmark">
                          <button
                            type="button"
                            onClick={() => void unmark()}
                            disabled={unmarking}
                            className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/[0.05] text-[16px] font-bold text-foreground disabled:opacity-60"
                          >
                            {unmarking ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : null}
                            {t('qt.unmark')}
                          </button>
                          <p className="mt-1.5 text-[12.5px] text-muted-foreground">{t('qt.unmarkNote')}</p>
                        </div>
                      )}
                      {tool === 'canvas' && <PostItLinks coverUrl={job.cover_url} fileUrl={downloadUrl(tool, job.id, { sessionId })} songUrl={job.extra_files?.includes('song') ? downloadUrl(tool, job.id, { sessionId, file: 'song' }) : null} />}
                      {isAuthenticated && <p className="mt-3 text-[13px] text-muted-foreground" data-testid="qt-in-gallery">{t('qt.inGallery')}</p>}
                      {job.cover_url && <CoverLink path={job.cover_url} />}
                      {job.family_url && <FamilyLink path={job.family_url} scenes={job.scene_count || 0} removed={job.removed_seconds || 0} />}
                      {tool === 'canvas' && <UseAsExample jobId={job.id} onAdded={() => void getCoverExamples(true).then(setExamples)} />}
                      {/* The link back to this tool, with the buyer's referral code. */}
                      <ShareCard studio="quick" path={spec.path} mediaUrl={downloadUrl(tool, job.id, { sessionId })} className="mt-4" />
                      </>
                    ) : opKey ? (
                      <div data-testid="qt-in-person" className="mt-4">
                        {opPaid ? (
                          <a href={downloadUrl(tool, job.id, { operatorKey: opKey })} className="flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-primary font-bold text-primary-foreground">
                            <Download className="h-5 w-5" aria-hidden /> {t('qt.op.yourCopy')}
                          </a>
                        ) : (
                          <>
                            <p className="text-[14px] text-muted-foreground">{t('qt.op.price')}</p>
                            <div className="mt-2 grid grid-cols-3 gap-2">
                              {IN_PERSON.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  onClick={() => setOpCents(c)}
                                  className={cn('min-h-[48px] rounded-xl border text-[17px] font-bold', opCents === c ? 'border-primary bg-primary/15 text-primary' : 'border-white/10 bg-white/[0.04] text-foreground')}
                                >
                                  {money(c)}
                                </button>
                              ))}
                            </div>
                            <button type="button" onClick={() => void showQr()} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-primary font-bold text-primary-foreground">
                              <QrCode className="h-5 w-5" aria-hidden /> {qr ? t('qt.op.newQr') : t('qt.op.showQr')}
                            </button>
                            {qr && (
                              <>
                                <img src={qr.src} alt={t('qt.op.scanAlt')} className="mx-auto mt-4 w-full max-w-[320px] rounded-2xl bg-white" />
                                <p className="mt-2 text-center text-[14px] text-muted-foreground">{t('qt.op.waiting', { price: money(qr.cents) })}</p>
                              </>
                            )}
                            <a href={downloadUrl(tool, job.id, { operatorKey: opKey })} className="mt-3 flex min-h-[48px] items-center justify-center rounded-2xl border border-white/15 text-[15px] font-semibold text-muted-foreground">
                              {t('qt.op.demo')}
                            </a>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => void buy()}
                          disabled={buying || !price}
                          data-testid="qt-buy"
                          className="mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-5 text-[17px] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7)] transition-transform active:scale-[0.98] disabled:opacity-70"
                        >
                          {buying ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : <Download className="h-5 w-5" aria-hidden />}
                          {buying ? t('qt.opening') : `${k('buy')} · ${payPrice ?? ''}`}
                        </button>
                        <CheckoutStrip methods="card" />
                      </>
                    )}
                    {error && <p className="mt-3 text-[15px] text-destructive" role="alert">{error}</p>}
                    <Link to={spec.path} className="mt-4 flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-white/10 text-[15px] font-semibold text-muted-foreground hover:text-foreground">
                      <RotateCcw className="h-4 w-4" aria-hidden /> {t('qt.another')}
                    </Link>
                  </div>
                )}
              </section>
              {studio && <div className="mt-8">{heroDetails}</div>}
            </div>

            {/* A REAL SAMPLE made with this tool (scripts/quicktools-samples.cjs); the Living Cover shows a live preview of the customer's own picture instead.
                The cover is always seen whole (owner 2026-10-18: "there is a moment where you can only see 3/4 of the living
                cover ... that should be completely visible on the page the entire time"): on a computer it is sized to the
                window's height and stays put while the options scroll; on a phone, once a picture is picked, it sits pinned
                at the top, smaller but whole, and the options scroll under it. */}
            <figure
              // The example phones are taller than a laptop window, so they scroll with the page instead of sticking.
              className={cn(!reelOn && 'lg:sticky lg:top-24',
                coverPinned && 'max-lg:sticky max-lg:top-[3.7rem] max-lg:z-20 max-lg:order-first max-lg:-mx-4 max-lg:border-b max-lg:border-white/10 max-lg:bg-background max-lg:px-4 max-lg:py-2',
                reelOn && !studio && 'max-lg:order-first')}
              data-testid="qt-sample"
            >
              {tool === 'canvas' ? (
                <div
                  className="relative mx-auto w-full"
                  style={{ maxWidth: reelOn
                    // Two phones side by side, each up to 230px (owner 2026-10-20: "this size is how big those smaller phones
                    // you made should be"); narrower only when the screen is.
                    ? '100%'
                    : coverPinned && phone ? 'calc(40dvh * 9 / 16)' : 'min(24rem, calc((100dvh - 9.5rem) * 9 / 16))' }}
                  data-testid="qt-live-preview"
                >
                  {pickedKind === 'video' && firstPicked && !editorShown ? (
                    <PickedClip file={firstPicked} />
                  ) : reelOn && examples ? (
                    <ExampleReel examples={examples} slide={(ex, hold) => <PhonePair example={ex} onSound={hold} />} />
                  ) : (
                    // Behind the open editor nothing is drawn twice.
                    editorShown ? <div className="w-full rounded-3xl bg-black" style={{ aspectRatio: previewAspect }} /> : livingCover('w-full rounded-3xl shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)]', { aspectRatio: previewAspect })
                  )}
                  <p className={cn('mt-2 text-center text-[12.5px] text-muted-foreground', coverPinned && 'max-lg:hidden')}>{reelOn ? t('qt.examples.reelNote') : k('previewNote')}</p>
                </div>
              ) : (
              <div className="relative mx-auto overflow-hidden rounded-3xl border border-white/10 bg-black" style={{ aspectRatio: sample.aspect, maxHeight: '72vh', maxWidth: sample.aspect === '16/9' ? '100%' : '24rem' }}>
                {sample.type === 'video' ? (
                  <video src={sample.src} poster={sample.poster} muted loop playsInline autoPlay preload="none" className="h-full w-full object-cover" />
                ) : (
                  <img src={sample.src} alt={k('sampleAlt')} loading="lazy" className="h-full w-full object-cover" />
                )}
              </div>
              )}
              {/* The spec plate (Studio Blueprint 05-A): what the download is, and what this sample is. */}
              <figcaption className={cn('mx-auto mt-3 max-w-sm rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-[11.5px] text-muted-foreground',
                // The Living Cover keeps the window for the cover: no plate under it on a computer, none under the pinned cover on a phone.
                tool === 'canvas' && 'lg:hidden', (coverPinned || reelOn) && 'max-lg:hidden')} data-testid="qt-spec">
                <p className="uppercase tracking-[0.12em] text-foreground/80">{t('qt.sampleCaption')}</p>
                <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <dt className="uppercase tracking-[0.1em]">{t('qt.spec.output')}</dt>
                  <dd className="text-foreground/85">{k('specOutput')}</dd>
                  <dt className="uppercase tracking-[0.1em]">{t('qt.spec.sample')}</dt>
                  <dd className="text-foreground/85">{k('specSample')}</dd>
                </dl>
              </figcaption>
            </figure>
          </div>

          <div className="mt-14 space-y-12 pb-16">
            {tool === 'canvas' && examples && examples.length > 0 && (
              // The owner's own examples, big and live (owner 2026-10-17).
              <section aria-labelledby="qt-examples">
                <h2 id="qt-examples" className="mb-6 text-center font-display text-[22px] font-bold sm:text-[28px]">{t('qt.examples.title')}</h2>
                <CoverExamples examples={examples} onRemoved={(id) => setExamples((x) => (x || []).filter((e) => e.id !== id))} />
              </section>
            )}
            {/* The Living Cover is paid before it is made: no "nothing is charged until download" next to it (design review #5). */}
            {!studio && <ApprovalRail kind="quick" />}
            <section aria-labelledby="qt-how" className="mx-auto max-w-4xl">
              <h2 id="qt-how" className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">{t('qt.howTitle')}</h2>
              <ol className="grid gap-3 sm:grid-cols-3">
                {['step1', 'step2', 'step3'].map((s, i) => (
                  <li key={s} className="rounded-2xl border border-border bg-card p-5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-[14px] font-bold text-primary" aria-hidden>
                      {i + 1}
                    </span>
                    <p className="mt-3 text-[15px] leading-relaxed text-foreground">{k(s, { price: price ?? '…' })}</p>
                  </li>
                ))}
              </ol>
            </section>
            <SystemChecks checks={studio ? ['quickNoAccount', 'quickDeleted', 'cardsToStripe'] : ['quickNoCharge', 'quickNoAccount', 'quickDeleted', 'cardsToStripe']} />
            <section aria-labelledby="qt-faq" className="mx-auto max-w-3xl">
              <h2 id="qt-faq" className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">{t('qt.faqTitle')}</h2>
              <div className="space-y-3">
                {['1', '2', '3', '4'].map((n) => (
                  <details key={n} className="group rounded-2xl border border-border bg-card p-5 open:border-primary/40">
                    <summary className="cursor-pointer list-none text-[15px] font-semibold text-foreground">{t(`qt.faq.q${n}`)}</summary>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{t(`qt.faq.a${n}`, { price: price ?? '…' })}</p>
                  </details>
                ))}
              </div>
            </section>
            {creator ? (
              <section aria-labelledby="qt-more">
                <h2 id="qt-more" className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">{t('qt.moreTitle')}</h2>
                <QuickToolCards exclude={tool} audience="creator" />
              </section>
            ) : (
              // Family tools recommend family things only (blueprint 02).
              <section aria-labelledby="qt-more" className="mx-auto max-w-3xl">
                <h2 id="qt-more" className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">{t('qt.moreFamily')}</h2>
                <StudioList only={['storybook', 'editor']} />
              </section>
            )}
            <SupportLine where={`quicktool_${tool}`} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/** The one question about the picture (owner 2026-10-21): someone in it (a person or animal, one or several) or not (a thing). */
function SubjectSwitch({ tool, field, value, onChange }: { tool: QuickToolKey; field: FieldSpec; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const base = `qt.tools.${tool}.fields.${field.name}`;
  return (
    <fieldset className="mt-4 min-w-0 first:mt-0" data-testid={`qt-choice-${field.name}`}>
      <legend className="text-[15px] font-semibold text-foreground">{t(`${base}.label`)}</legend>
      <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{t(`${base}.hint`)}</p>
      <input type="hidden" name={field.name} value={value} />
      <div className="mt-2.5 grid grid-cols-2 gap-1 rounded-2xl bg-white/[0.05] p-1" role="radiogroup">
        {(field.options || []).map((o) => (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={value === o}
            onClick={() => onChange(o)}
            className={cn('flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-3 text-[14.5px] font-semibold transition-colors',
              value === o ? 'bg-primary text-primary-foreground shadow-[0_6px_18px_-8px_hsl(var(--primary)/0.8)]' : 'text-muted-foreground hover:text-foreground')}
          >
            {value === o && <Check className="h-4 w-4" aria-hidden />}
            {t(`${base}.options.${o}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

type EditorPane = 'post' | 'found' | 'crop' | 'song' | 'behind' | 'move' | 'overlays' | 'finish';
// The swipe's overlay list when the page has no overlay field (hooks run on every page).
const NO_MULTI: FieldSpec = { name: 'float', type: 'multi' };
const ADJUST_ICONS: Record<string, LucideIcon> = { brightness: SunMedium, amount: Layers, glowlevel: Sun, bling: Sparkles, cloth: Shirt, grain: Film, bw: Contrast };

/** A round icon button in the editor's top bar. */
function EditorIconButton({ label, testid, children, ...rest }: { label: string; testid: string; children: ReactNode } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return (
    <button type="button" aria-label={label} title={label} data-testid={testid} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground/90 transition-colors hover:bg-white/[0.08] disabled:opacity-30 disabled:hover:bg-transparent" {...rest}>
      {children}
    </button>
  );
}

/** One tool in the Adjust sheet (Instagram's Edit row): a round icon, a short name and where it stands. */
function AdjustTile({ icon: Icon, label, value, on, onClick, testid, children }: { icon: LucideIcon; label: string; value: string; on: boolean; onClick: () => void; testid: string; children?: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={testid} className="group flex min-w-0 flex-col items-center gap-1.5 text-center">
      {children}
      <span aria-hidden className={cn('flex h-14 w-14 items-center justify-center rounded-full transition-colors', on ? 'bg-primary text-primary-foreground' : 'bg-white/[0.07] text-foreground group-hover:bg-white/[0.12]')}>
        <Icon className="h-6 w-6" />
      </span>
      <span className="text-[12.5px] font-semibold leading-tight text-foreground">{label}</span>
      <span className="text-[11px] tabular-nums text-muted-foreground">{value}</span>
    </button>
  );
}

/** An on/off row with a switch (the site mark). */
function ToggleRow({ icon: Icon, name, label, checked, onChange }: { icon: LucideIcon; name: string; label: string; checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <label className="group mt-4 flex min-h-[52px] cursor-pointer items-center gap-3 rounded-2xl bg-white/[0.05] px-3.5 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary" data-testid={`qt-switch-${name}`}>
      <input type="checkbox" name={name} checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <Icon className="h-5 w-5 shrink-0 text-muted-foreground group-has-[:checked]:text-primary" aria-hidden />
      <span className="flex-1 text-[14px] font-semibold text-foreground">{label}</span>
      <span aria-hidden className="relative h-6 w-10 shrink-0 rounded-full bg-white/15 transition-colors group-has-[:checked]:bg-primary">
        <i className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform group-has-[:checked]:translate-x-4" />
      </span>
    </label>
  );
}

function DropZone({
  tool,
  upload,
  files,
  onPick,
  voiceOff,
  studio = false,
  locked = null,
}: {
  tool: QuickToolKey;
  upload: UploadSpec;
  files?: File[];
  onPick: (field: string, files: FileList | null) => void;
  voiceOff?: boolean;
  /** The Living Cover's editor look: a big obvious button, then the picked file as a row with a thumbnail. */
  studio?: boolean;
  /** Signed out (owner 2026-10-27, registered first): the same obvious button, saying sign up or log in, and going there. */
  locked?: (() => void) | null;
}) {
  const { t } = useTranslation();
  const has = !!files?.length;
  const base = `qt.tools.${tool}.uploads.${upload.field}`;
  const label = voiceOff && upload.field === 'voiceover' ? t(`${base}.labelRequired`) : t(`${base}.label`);
  const hint = locked ? t('qt.signInFirst.hint') : voiceOff && upload.field === 'voiceover' ? t(`${base}.hintRequired`) : t(`${base}.hint`);
  // Signed out: a tap or a drop goes to sign up (and back here), nothing is picked.
  const lock = locked ? { onClick: (e: { preventDefault: () => void }) => { e.preventDefault(); locked(); }, 'data-locked': 'true' } : {};
  const [over, setOver] = useState(false);
  const first = files?.[0];
  const thumb = useMemo(() => (studio && first && first.type.startsWith('image') ? URL.createObjectURL(first) : null), [studio, first]);
  useEffect(() => () => { if (thumb) URL.revokeObjectURL(thumb); }, [thumb]);
  if (studio) {
    const drop = {
      onDragOver: (e: DragEvent) => { e.preventDefault(); setOver(true); },
      onDragLeave: () => setOver(false),
      onDrop: (e: DragEvent) => { e.preventDefault(); setOver(false); if (locked) locked(); else if (e.dataTransfer.files?.length) onPick(upload.field, e.dataTransfer.files); },
    };
    const input = locked ? null : <input type="file" accept={upload.accept} multiple={!!upload.multiple} className="sr-only" onChange={(e) => onPick(upload.field, e.target.files)} />;
    const pill = (text: string, Icon: LucideIcon) => (
      <span className="inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-full bg-white/[0.09] px-3.5 text-[13px] font-semibold text-foreground transition-colors group-hover:bg-white/[0.15]">
        <Icon className="h-4 w-4" aria-hidden />{text}
      </span>
    );
    if (has && first) {
      return (
        <label data-testid={`qt-drop-${upload.field}`} {...drop} className={cn('group mt-3 flex cursor-pointer items-center gap-3 rounded-2xl bg-white/[0.05] p-2.5 first:mt-0', over && 'ring-2 ring-primary')}>
          {input}
          {thumb ? (
            <img src={thumb} alt="" className="h-24 w-[54px] shrink-0 rounded-xl object-cover" />
          ) : (
            <span aria-hidden className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              {first.type.startsWith('video') ? <Film className="h-5 w-5" /> : upload.optional ? <Music className="h-5 w-5" /> : <Check className="h-5 w-5" />}
            </span>
          )}
          <span className="block min-w-0 flex-1">
            {upload.optional && <span className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-accent"><Check className="h-3.5 w-3.5" aria-hidden />{label.replace(/\s*\(.*\)\s*$/, '')}</span>}
            {/* The picture is the confirmation (design review #6); a song shows its name. */}
            {upload.optional && <span className="mt-0.5 block truncate text-[14px] font-semibold text-foreground">{files!.length > 1 ? t('qt.filesChosen', { n: files!.length }) : first.name}</span>}
          </span>
          {pill(t(`qt.tools.${tool}.studio.change`), Replace)}
        </label>
      );
    }
    if (upload.optional) {
      return (
        <label data-testid={`qt-drop-${upload.field}`} {...drop} {...lock} className={cn('group mt-3 flex cursor-pointer items-center gap-3 rounded-2xl bg-white/[0.05] p-3 transition-colors first:mt-0 hover:bg-white/[0.08]', over && 'ring-2 ring-primary')}>
          {input}
          <span aria-hidden className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,hsl(var(--primary)/0.35),hsl(var(--accent)/0.3))] text-foreground"><Music className="h-5 w-5" /></span>
          <span className="block min-w-0 flex-1">
            <span className="block text-[14.5px] font-semibold text-foreground">{label}</span>
            <span className="mt-0.5 block text-[12px] leading-snug text-muted-foreground">{t(`qt.tools.${tool}.studio.songHint`, { defaultValue: hint })}</span>
          </span>
          {locked ? pill(t('qt.signInFirst.short'), LogIn) : pill(t(`qt.tools.${tool}.studio.addSong`), Plus)}
        </label>
      );
    }
    return (
      <label data-testid={`qt-drop-${upload.field}`} {...drop} {...lock} className="group mt-3 block cursor-pointer rounded-2xl bg-[linear-gradient(135deg,#F9D27E,hsl(var(--primary)),hsl(var(--accent)))] p-[1.5px] first:mt-0">
        {input}
        <span className={cn('flex min-h-[clamp(260px,46dvh,420px)] flex-col items-center justify-center rounded-[15px] bg-[hsl(220_20%_8%)] px-4 py-7 text-center transition-colors', over && 'bg-[hsl(220_20%_12%)]')}>
          <span aria-hidden className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7)]">
            <ImagePlus className="h-7 w-7" />
          </span>
          <span className="mt-3 block text-[17px] font-semibold text-foreground">{label}</span>
          <span className="mt-1 block max-w-sm text-[13px] leading-snug text-muted-foreground">{hint}</span>
          <span className="mt-4 inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 text-[15px] font-bold text-[#10131a] transition-transform group-active:scale-[0.98]">
            {locked ? <><LogIn className="h-5 w-5" aria-hidden />{t('qt.signInFirst.button')}</> : <><Upload className="h-5 w-5" aria-hidden />{t(`qt.tools.${tool}.studio.upload`)}</>}
          </span>
          {!locked && <span className="mt-2 hidden text-[12px] text-muted-foreground lg:block">{t(`qt.tools.${tool}.studio.drop`)}</span>}
        </span>
      </label>
    );
  }
  return (
    <label
      data-testid={`qt-drop-${upload.field}`}
      {...lock}
      className={cn(
        'mt-3 block cursor-pointer rounded-2xl border-[1.5px] border-dashed text-center transition-colors first:mt-0',
        upload.optional ? 'flex items-center gap-3 p-4 text-left' : 'px-4 py-7',
        has ? 'border-primary bg-primary/[0.06]' : 'border-white/20 bg-white/[0.02] hover:border-primary/60',
      )}
    >
      {!locked && (
        <input
          type="file"
          accept={upload.accept}
          multiple={!!upload.multiple}
          className="sr-only"
          onChange={(e) => onPick(upload.field, e.target.files)}
        />
      )}
      <span
        aria-hidden
        className={cn('flex shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary', upload.optional ? 'h-10 w-10' : 'mx-auto mb-3 h-14 w-14')}
      >
        {has ? <Check className="h-6 w-6" /> : <Upload className="h-6 w-6" />}
      </span>
      <span className="block min-w-0">
        <span className={cn('block font-semibold text-foreground', upload.optional ? 'text-[15px]' : 'text-[18px]')}>
          {locked ? t('qt.signInFirst.button') : has ? (files!.length > 1 ? t('qt.filesChosen', { n: files!.length }) : files![0].name) : label}
        </span>
        <span className="mt-1 block text-[13.5px] text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

/** One-of choice cards: a moving example on each when the field has examples, the price on the song lengths. */
function ChoiceField({
  tool,
  field,
  value,
  onChange,
  prices,
}: {
  tool: QuickToolKey;
  field: FieldSpec;
  value: string;
  onChange: (v: string) => void;
  prices: Record<string, string>;
}) {
  const { t } = useTranslation();
  const base = `qt.tools.${tool}.fields.${field.name}`;
  const rich = !!field.examples;
  if (!rich) {
    // A segmented control (the song version's length): the name, and its price under it.
    return (
      <fieldset className="mt-4 min-w-0" data-testid={`qt-choice-${field.name}`}>
        <legend className="mb-2 text-[14px] font-medium text-muted-foreground">{t(`${base}.label`)}</legend>
        <div className={cn('grid gap-1 rounded-2xl bg-white/[0.05] p-1 pt-2.5', (field.options || []).length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
          {(field.options || []).map((o) => {
            const [name, cost] = t(`${base}.options.${o}`, { price: prices[o] }).split(' · ');
            // "Best value · Save $4" on the Full pack (owner 2026-10-22, the research's ladder).
            const badge = t(`${base}.badges.${o}`, { defaultValue: '', save: prices[`${o}Save`] });
            return (
              <label key={o} className="relative cursor-pointer rounded-xl px-2 py-2.5 text-center transition-colors has-[:checked]:bg-white/[0.12] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary">
                <input type="radio" name={field.name} value={o} checked={value === o} onChange={() => onChange(o)} className="sr-only" />
                {/* Above the option's name, never over it (owner 2026-10-23: he saw no choice). */}
                {badge && <span className="absolute inset-x-0.5 -top-2 truncate whitespace-nowrap rounded-full bg-primary px-1 py-px text-[9px] font-bold uppercase leading-tight tracking-[0.02em] text-primary-foreground" data-testid={`qt-badge-${o}`}>{badge}</span>}
                <span className="block text-[13.5px] font-semibold leading-tight text-foreground">{name}</span>
                {cost && <span className="mt-0.5 block text-[12.5px] font-semibold tabular-nums text-primary">{cost}</span>}
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }
  return (
    <fieldset className="mt-5" data-testid={`qt-choice-${field.name}`}>
      <legend className="mb-2 text-[14px] font-medium text-muted-foreground">{t(`${base}.label`)}</legend>
      {t(`${base}.hint`, { defaultValue: '' }) && <p className="-mt-1 mb-2 text-[12.5px] leading-snug text-muted-foreground">{t(`${base}.hint`)}</p>}
      <div className={cn('grid gap-2', rich ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-2')}>
        {(field.options || []).map((o) => (
          <label
            key={o}
            className="cursor-pointer rounded-2xl border border-white/10 bg-white/[0.03] p-2 has-[:checked]:border-primary/60 has-[:checked]:bg-primary/10"
          >
            {rich && (field.examples?.[o] ? (
              <video src={field.examples[o]} autoPlay muted loop playsInline preload="metadata" aria-hidden className="aspect-[9/16] w-full rounded-xl bg-black object-cover" />
            ) : (
              <span aria-hidden className="block aspect-[9/16] w-full rounded-xl bg-black" />
            ))}
            <span className={cn('flex items-center gap-2', rich && 'mt-2')}>
              <input type="radio" name={field.name} value={o} checked={value === o} onChange={() => onChange(o)} className="h-4 w-4 shrink-0 accent-[hsl(var(--primary))]" />
              <span className="text-[13.5px] font-semibold leading-tight text-foreground">{t(`${base}.options.${o}`, { price: prices[o] })}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Every overlay as the picker shows it: the engine's own kinds and the owner's first clips (tools.json's options,
 * named by the locale) placed in their groups like everything else (overlay-library.json drawn; no "Signature",
 * owner 2026-10-21), then the library's clips; all in the groups' order, the engine's own first in each. */
function useOverlayItems(tool: QuickToolKey, field: FieldSpec): PickItem[] {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  return useMemo(() => {
    const base = `qt.tools.${tool}.fields.${field.name}`;
    const other = lang.startsWith('es') ? 'en' : 'es';
    const options = field.options || [];
    const own: PickItem[] = options.flatMap((o) => {
      const lib = LIB_OVERLAYS[o];
      const drawn = LIB_DRAWN[o];
      if (!lib && !drawn) return [];
      return [{ id: o, label: t(`${base}.options.${o}`), group: lib ? lib.group : drawn.group, thumb: lib ? lib.thumb : drawn.thumb, preview: lib?.preview, blend: lib ? lib.blend : drawn.blend, words: `${t(`${base}.options.${o}`, { lng: other })} ${o}` }];
    });
    const rest: PickItem[] = Object.entries(LIB_OVERLAYS).filter(([id]) => !options.includes(id))
      .map(([id, o]) => ({ id, label: libName(o, lang), group: o.group, thumb: o.thumb, preview: o.preview, blend: o.blend, words: `${o[other]} ${o.kind}` }));
    const order = new Map(LIBRARY.groups.map((g, i) => [g.id, i]));
    return [...own, ...rest].sort((a, b) => (order.get(a.group) ?? 99) - (order.get(b.group) ?? 99));
  }, [tool, field, lang, t]);
}

function useBackdropItems(): PickItem[] {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  return useMemo(() => {
    const other = lang.startsWith('es') ? 'en' : 'es';
    const order = new Map(LIBRARY.backdropGroups.map((g, i) => [g.id, i]));
    return Object.entries(LIB_BACKDROPS).map(([id, b]) => ({ id, label: libName(b, lang), group: b.group, thumb: b.thumb, preview: b.preview, words: `${b[other]} ${b.kind}` }))
      .sort((a, b) => (order.get(a.group) ?? 99) - (order.get(b.group) ?? 99));
  }, [lang]);
}

/** The live backdrops picked (a comma list, bottom first), up to MAX_BACKDROPS (particles.js, owner 2026-10-27). */
const MAX_BACKDROPS = 3;
const backdropIds = (v?: string) => [...new Set((v || '').split(',').filter((id) => LIB_BACKDROPS[id]))].slice(0, MAX_BACKDROPS);

/** Each picked overlay's see-through slider, sent as "kind:60,kind:35" (percent shown; absent = 100, solid). */
function parseOpacity(v: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const pair of (v || '').split(',')) {
    const [k, n] = pair.split(':');
    const x = Number(n);
    if (k && n !== undefined && n !== '' && Number.isFinite(x)) out[k] = Math.max(5, Math.min(100, Math.round(x)));
  }
  return out;
}
const formatOpacity = (o: Record<string, number>) => Object.entries(o).filter(([, n]) => n < 100).map(([k, n]) => `${k}:${n}`).join(',');

/**
 * Where each picked overlay sits (owner 2026-10-24, moved and resized like InShot's; 2026-10-27 turned too), sent as
 * "kind:x:y:s[:r],..." (the server's particles.js floatPlace reads it; float-scene placeBox draws it); one left where it
 * was is left out.
 */
function parseSpot(v: string): Record<string, Place> {
  const out: Record<string, Place> = {};
  for (const item of (v || '').split(',')) {
    const [k, ...n] = item.split(':');
    const [x, y, sz, r] = n.map(Number);
    if (k && (n.length === 3 || n.length === 4) && [x, y, sz].every(Number.isFinite)) out[k] = { x, y, s: sz, ...(n.length === 4 && Number.isFinite(r) && r ? { r } : {}) };
  }
  return out;
}
const formatSpot = (o: Record<string, Place>) => Object.entries(o).filter(([, p]) => !placeIsNone(p))
  .map(([k, p]) => `${k}:${p.x}:${p.y}:${p.s}${placeTurn(p) ? `:${Math.round(placeTurn(p) * 10) / 10}` : ''}`).join(',');
/** Only the kinds still picked keep their opacity and their place. */
const keepPicked = (choices: Record<string, string>, list: string[]) => ({
  opacity: formatOpacity(Object.fromEntries(Object.entries(parseOpacity(choices.opacity ?? '')).filter(([id]) => list.includes(id)))),
  spot: formatSpot(Object.fromEntries(Object.entries(parseSpot(choices.spot ?? '')).filter(([id]) => list.includes(id)))),
});

/**
 * Several at once, up to field.picks, sent as a comma list in a hidden input ('none' when nothing is picked).
 * A tap puts one straight on the big preview; what is on is listed above the tray, each with its own see-through
 * slider (owner 2026-10-21: "most of the overlays are too dark and cover the image ... we need a sliding
 * transparency").
 */
function MultiField({ tool, field, value, onChange, opacity, onOpacity, bare = false, focus: focusIn, onFocus, spot = '', onSpot }: {
  tool: QuickToolKey; field: FieldSpec; value: string; onChange: (v: string) => void; opacity: string; onOpacity: (v: string) => void; bare?: boolean;
  /** The editor's overlay being placed (it draws the outline on the picture); kept here when the caller does not keep it. */
  focus?: string | null; onFocus?: (id: string | null) => void;
  /** Where each overlay sits ("kind:x:y:s", parseSpot); only the editor moves them. */
  spot?: string; onSpot?: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const base = `qt.tools.${tool}.fields.${field.name}`;
  const lib = `qt.tools.${tool}.fields.library`;
  const picked = value.split(',').filter((v) => v && v !== 'none');
  const max = field.picks || 3;
  const items = useOverlayItems(tool, field);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const see = parseOpacity(opacity);
  const spots = parseSpot(spot);
  const set = (next: string[]) => {
    onChange(next.join(',') || 'none');
    const kept = Object.fromEntries(Object.entries(see).filter(([k]) => next.includes(k)));
    onOpacity(formatOpacity(kept));
    if (onSpot && spot) onSpot(formatSpot(Object.fromEntries(Object.entries(spots).filter(([k]) => next.includes(k)))));
  };
  // The one whose strength slider is open (Instagram/CapCut): it opens the moment you pick, and again when a picked
  // tile is tapped again, so the slider is never something to go looking for. In the editor it is also the one placed
  // on the picture (moved and resized there, owner 2026-10-24).
  const [ownFocus, setOwnFocus] = useState<string | null>(null);
  const focus = onFocus ? focusIn ?? null : ownFocus;
  const setFocus = onFocus || setOwnFocus;
  const toggle = (o: string) => {
    const on = !picked.includes(o);
    set(on ? [...picked, o] : picked.filter((p) => p !== o));
    setFocus(on ? o : focus === o ? null : focus);
  };
  const focused = focus && picked.includes(focus) ? byId.get(focus) : null;
  const strength = focused && (
    // One slim row over the bottom of the picture (Instagram's strength slider): it must never hide the picture.
    <div className="fixed inset-x-3 bottom-[calc(var(--qt-float-bottom,4.75rem)+env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md items-center gap-2.5 rounded-full bg-black/70 py-1.5 pl-3 pr-1.5 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.9)] ring-1 ring-white/10 backdrop-blur" role="dialog" aria-label={t(`${lib}.opacityOf`, { name: focused.label })} data-testid="qt-strength">
      <span className="max-w-[6.5rem] shrink-0 truncate text-[12.5px] font-semibold text-foreground">{focused.label.replace(/\s+\d+$/, '')}</span>
      <input
        type="range"
        min={5}
        max={100}
        step={1}
        value={see[focused.id] ?? 100}
        onChange={(e) => onOpacity(formatOpacity({ ...see, [focused.id]: Number(e.target.value) }))}
        aria-label={t(`${lib}.opacityOf`, { name: focused.label })}
        className="h-7 min-w-0 flex-1 cursor-pointer accent-[hsl(var(--primary))]"
      />
      <span className="w-9 shrink-0 text-right text-[12.5px] font-bold tabular-nums text-primary">{see[focused.id] ?? 100}%</span>
      {/* Moved or resized on the picture: put it back over the whole picture. */}
      {onSpot && spots[focused.id] && !placeIsNone(spots[focused.id]) && (
        <button type="button" onClick={() => onSpot(formatSpot(Object.fromEntries(Object.entries(spots).filter(([k]) => k !== focused.id))))} aria-label={t(`${lib}.resetPlace`, { name: focused.label })} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-foreground" data-testid="qt-place-reset">
          <RotateCcw className="h-4 w-4" aria-hidden />
        </button>
      )}
      <button type="button" onClick={() => setFocus(null)} aria-label={t(`${lib}.done`)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-foreground" data-testid="qt-strength-done">
        <Check className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
  const groups = LIBRARY.groups.map((g) => ({ id: g.id, label: libName(g, i18n.language || 'en') }));
  const body = (
    <>
      {/* Always shown (owner 2026-10-21: he never saw the transparency sliders, which only appeared after a pick). */}
      {(
        <div className="mb-3 rounded-2xl bg-white/[0.04] px-3 py-2.5" data-testid={`qt-library-${field.name}-picked`}>
          <div className="mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[12.5px] font-semibold uppercase tracking-[0.08em] text-foreground/85"><SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden />{t(`${lib}.applied`)}
              {/* The limit, always in view (owner 2026-10-21: "there should also be a advisement somewhere about the limit"). */}
              <span className={cn('ml-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold normal-case tracking-normal tabular-nums', picked.length >= max ? 'bg-primary text-primary-foreground' : 'bg-white/10 text-foreground/80')} data-testid="qt-overlay-limit">{t(`${lib}.limit`, { n: picked.length, max })}</span></span>
            {picked.length > 0 && <button type="button" onClick={() => set([])} className="min-h-[32px] rounded-full px-2.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground">{t(`${lib}.clearAll`)}</button>}
          </div>
          {picked.length === 0 && <p className="pt-1 text-[12.5px] leading-snug text-muted-foreground" data-testid="qt-opacity-empty">{t(`${lib}.appliedEmpty`, { max })}</p>}
          {/* What is on, as slim chips (owner 2026-10-21: the tiles must stay big and in view): tap a chip for its
              opacity slider over the picture, x to take it off. */}
          {picked.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {picked.map((id) => {
                const name = (byId.get(id)?.label || id).replace(/\s+\d+$/, '');
                return (
                  <span key={id} className={cn('inline-flex items-center rounded-full', focus === id ? 'bg-primary/20 ring-1 ring-primary/60' : 'bg-white/[0.08]')} data-kind={id}>
                    <button type="button" onClick={() => setFocus(focus === id ? null : id)} aria-label={t(`${lib}.opacityOf`, { name })} data-testid={`qt-opacity-${id}`} className="min-h-[34px] pl-3 pr-1.5 text-[13px] font-semibold text-foreground">
                      {name} <span className="font-bold tabular-nums text-primary">{see[id] ?? 100}%</span>
                    </button>
                    <button type="button" onClick={() => toggle(id)} aria-label={t(`${base}.remove`, { name })} className="flex h-[34px] w-8 items-center justify-center rounded-full text-foreground/70 hover:text-foreground">
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
      <LibraryPicker
        items={items}
        groups={groups}
        picked={picked}
        max={max}
        onToggle={toggle}
        onNone={() => { set([]); setFocus(null); }}
        noneLabel={t(`${lib}.none`)}
        searchLabel={t(`${base}.search`)}
        noMatch={t(`${base}.noMatch`)}
        fullLabel={t(`${lib}.full`, { max })}
        fullBar={{ title: (n, m) => t(`${lib}.fullBar`, { n, max: m }), remove: (name) => t(`${base}.remove`, { name }) }}
        allLabel={t(`${lib}.all`)}
        clearLabel={t(`${lib}.clearSearch`)}
        offLabel={t(`${lib}.tapOff`)}
        testid={`qt-library-${field.name}`}
      />
      {strength}
    </>
  );
  if (bare) return <div className="min-w-0" data-testid={`qt-multi-${field.name}`}>{body}</div>;
  return (
    <fieldset className="mt-5 min-w-0" data-testid={`qt-multi-${field.name}`}>
      <legend className="mb-1 text-[14px] font-medium text-muted-foreground">{t(`${base}.label`)}</legend>
      <p className="mb-2 text-[12.5px] text-muted-foreground">{t(`${base}.hint`, { max })}</p>
      <input type="hidden" name={field.name} value={picked.join(',') || 'none'} />
      {body}
    </fieldset>
  );
}

/**
 * STEP 1 OF A BACKDROP: THE CUSTOMER'S OWN DECISION TO REMOVE THEIR BACKGROUND (owner 2026-10-21: "how would you or
 * i or anyone know if someone wants their background removed from their image?"; "thats their choice to make not
 * yours"; "before they can choose a backdrop they must make the decision to crop their image"). Nothing is removed
 * until they press the button; a photo they cut out themselves (a PNG with a see-through background, ChopBox's
 * Remove background) counts as their decision already made. Only then do the backdrops unlock.
 */
function CutoutStep({ tool, subject, depth, busy, picked, cut, declined, onCut }: {
  tool: QuickToolKey; subject: string; depth: DepthPreview | null; busy: boolean; picked: boolean; cut: boolean; declined: boolean; onCut: (on: boolean | 'no') => void;
}) {
  const { t, i18n } = useTranslation();
  const base = `qt.tools.${tool}.fields.backdrop`;
  const chopbox = `/editor/${EDITOR_PAGES.removeBackground[(i18n.language || 'en').startsWith('es') ? 'es' : 'en']}`;
  const own = depth?.why === 'cut out already';
  const state = !picked ? 'needPhoto' : subject === 'scene' ? 'scene' : !cut && !own ? (declined ? 'declined' : 'ask') : busy ? 'working' : !depth ? 'unknown' : depth.depth ? (own ? 'doneOwn' : 'done') : 'noSubject';
  const good = state === 'done' || state === 'doneOwn';
  const bad = state === 'noSubject' || state === 'unknown';
  const chopLink = (
    <a href={chopbox} target="_blank" rel="noopener" className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full bg-white/[0.08] px-4 text-[14px] font-semibold text-foreground hover:bg-white/[0.14]" data-testid="qt-cutout-chopbox">
      <Scissors className="h-4 w-4" aria-hidden />
      {t(`${base}.chopbox`)}
    </a>
  );
  return (
    <div role="status" className={cn('mb-4 rounded-2xl p-3.5', state === 'ask' ? 'bg-white/[0.05] ring-1 ring-primary/30' : bad ? 'bg-primary/10 ring-1 ring-primary/40' : 'bg-white/[0.04]')} data-testid="qt-backdrop-note" data-state={state}>
      {/* The rule first (owner 2026-10-21: "inform them that the cropping process is whats needed to use a backdrop"). */}
      {(state === 'ask' || state === 'needPhoto') && <p className="mb-1.5 text-[15px] font-bold leading-snug text-foreground" data-testid="qt-cutout-rule">{t(`${base}.cut.rule`)}</p>}
      <p className="flex items-start gap-2 text-[14px] leading-snug text-foreground">
        {state === 'working' ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
          : good ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
            : <Scissors className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />}
        <span>{t(`${base}.cut.${state}`)}</span>
      </p>
      {state === 'ask' && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <button type="button" onClick={() => onCut(true)} data-testid="qt-cutout-go" className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-[14px] font-bold text-primary-foreground">
            <Scissors className="h-4 w-4" aria-hidden />
            {t(`${base}.cutGo`)}
          </button>
          {chopLink}
          <button type="button" onClick={() => onCut('no')} data-testid="qt-cutout-no" className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-white/[0.08] px-4 text-[14px] font-semibold text-foreground hover:bg-white/[0.14] sm:col-span-2">
            {t(`${base}.cutNo`)}
          </button>
          <p className="text-[12px] leading-snug text-muted-foreground sm:col-span-2">{t(`${base}.chopboxHint`)}</p>
        </div>
      )}
      {state === 'declined' && (
        <div className="mt-2 pl-6">
          <button type="button" onClick={() => onCut(true)} data-testid="qt-cutout-change" className="inline-flex min-h-[40px] items-center rounded-full bg-white/[0.08] px-3.5 text-[13px] font-semibold text-foreground hover:bg-white/[0.14]">
            {t(`${base}.cutGo`)}
          </button>
        </div>
      )}
      {(bad || good) && !own && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2 pl-6">
          {bad && chopLink}
          <button type="button" onClick={() => onCut('no')} data-testid="qt-cutout-undo" className="inline-flex min-h-[40px] items-center rounded-full px-3 text-[13px] font-semibold text-muted-foreground hover:text-foreground">
            {t(`${base}.cutUndo`)}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A LIVE BACKDROP (owner 2026-10-20: "some live backdrops moving in the background would bring this to the next
 * level"): one moving scene from the library, played behind the person, animal or thing in the picture in place of
 * its own background (canvas.js backdropPlate).
 */
function BackdropField({ tool, field, value, onChange, subject, depth, depthBusy, picked, cut, onCut, declined = false, photo = null, bare = false,
  opacity = '', onOpacity, spot = '', onSpot, focus: focusIn, onFocus }: {
  tool: QuickToolKey; field: FieldSpec; value: string; onChange: (v: string) => void; subject: string; depth: DepthPreview | null; depthBusy: boolean; picked: boolean;
  cut: boolean; onCut: (on: boolean | 'no') => void; declined?: boolean; photo?: string | null; bare?: boolean;
  /** Each backdrop's see-through slider ("id:60", as the overlays') and place ("id:x:y:s"); only the editor moves them. */
  opacity?: string; onOpacity?: (v: string) => void; spot?: string; onSpot?: (v: string) => void;
  /** The backdrop whose slider is open (in the editor, also the one placed on the picture). */
  focus?: string | null; onFocus?: (id: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const base = `qt.tools.${tool}.fields.${field.name}`;
  const lib = `qt.tools.${tool}.fields.library`;
  const lang = i18n.language || 'en';
  const items = useBackdropItems();
  const [ownFocus, setOwnFocus] = useState<string | null>(null);
  if (!items.length) return null;
  const groups = LIBRARY.backdropGroups.map((g) => ({ id: g.id, label: libName(g, lang) }));
  // The backdrops unlock only once the background is removed (the customer's step 1) and the cut-out worked.
  const open = cut && subject !== 'scene' && Boolean(depth?.depth);
  // Up to three, laid one over another, the first at the bottom (owner 2026-10-27: "we can allow users more than one backdrop").
  const chosen = open ? backdropIds(value) : [];
  const see = parseOpacity(opacity);
  const spots = parseSpot(spot);
  const focus = onFocus ? focusIn ?? null : ownFocus;
  const setFocus = onFocus || setOwnFocus;
  const name = (id: string) => (items.find((i) => i.id === id)?.label || id).replace(/\s+\d+$/, '');
  // Only the ones still picked keep their slider and place.
  const set = (next: string[]) => {
    onChange(next.join(','));
    if (onOpacity) onOpacity(formatOpacity(Object.fromEntries(Object.entries(see).filter(([k]) => next.includes(k)))));
    if (onSpot) onSpot(formatSpot(Object.fromEntries(Object.entries(spots).filter(([k]) => next.includes(k)))));
  };
  const toggle = (id: string) => {
    const on = !chosen.includes(id);
    if (on && chosen.length >= MAX_BACKDROPS) return;
    set(on ? [...chosen, id] : chosen.filter((c) => c !== id));
    setFocus(on ? id : focus === id ? null : focus);
  };
  const focused = focus && chosen.includes(focus) ? focus : null;
  // Its see-through slider over the bottom of the picture, as the overlays' (MultiField): never hiding the picture.
  const strength = focused && onOpacity && (
    <div className="fixed inset-x-3 bottom-[calc(var(--qt-float-bottom,4.75rem)+env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md items-center gap-2.5 rounded-full bg-black/70 py-1.5 pl-3 pr-1.5 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.9)] ring-1 ring-white/10 backdrop-blur" role="dialog" aria-label={t(`${lib}.opacityOf`, { name: name(focused) })} data-testid="qt-backdrop-strength">
      <span className="max-w-[6.5rem] shrink-0 truncate text-[12.5px] font-semibold text-foreground">{name(focused)}</span>
      <input
        type="range"
        min={5}
        max={100}
        step={1}
        value={see[focused] ?? 100}
        onChange={(e) => onOpacity(formatOpacity({ ...see, [focused]: Number(e.target.value) }))}
        aria-label={t(`${lib}.opacityOf`, { name: name(focused) })}
        className="h-7 min-w-0 flex-1 cursor-pointer accent-[hsl(var(--primary))]"
      />
      <span className="w-9 shrink-0 text-right text-[12.5px] font-bold tabular-nums text-primary">{see[focused] ?? 100}%</span>
      {onSpot && spots[focused] && !placeIsNone(spots[focused]) && (
        <button type="button" onClick={() => onSpot(formatSpot(Object.fromEntries(Object.entries(spots).filter(([k]) => k !== focused))))} aria-label={t(`${lib}.resetPlace`, { name: name(focused) })} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-foreground" data-testid="qt-backdrop-place-reset">
          <RotateCcw className="h-4 w-4" aria-hidden />
        </button>
      )}
      <button type="button" onClick={() => setFocus(null)} aria-label={t(`${lib}.done`)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-foreground" data-testid="qt-backdrop-strength-done">
        <Check className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
  const body = (
    <>
      <CutoutStep tool={tool} subject={subject} depth={depth} busy={depthBusy} picked={picked} cut={cut} declined={declined} onCut={onCut} />
      {open && chosen.length > 0 && (
        <div className="mb-4 rounded-2xl bg-white/[0.04] px-3 py-2.5" data-testid={`qt-library-${field.name}-picked`}>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-[12.5px] font-semibold uppercase tracking-[0.08em] text-foreground/85"><SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden />{t(`${lib}.applied`)}
              <span className={cn('ml-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold normal-case tracking-normal tabular-nums', chosen.length >= MAX_BACKDROPS ? 'bg-primary text-primary-foreground' : 'bg-white/10 text-foreground/80')} data-testid="qt-backdrop-limit">{t(`${lib}.limit`, { n: chosen.length, max: MAX_BACKDROPS })}</span></span>
            <button type="button" onClick={() => { set([]); setFocus(null); }} className="min-h-[32px] rounded-full px-2.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground">{t(`${lib}.clearAll`)}</button>
          </div>
          {/* Each picked one as a chip: tap it for its see-through slider (and, in the editor, to move and resize it on the picture), x to take it off. */}
          <div className="flex flex-wrap gap-1.5">
            {chosen.map((id) => {
              const thumb = items.find((i) => i.id === id)?.thumb;
              return (
                <span key={id} className={cn('inline-flex min-h-[36px] items-center rounded-full pl-1 pr-0.5 text-[12.5px] font-semibold', focused === id ? 'bg-primary/20 text-primary ring-1 ring-primary/50' : 'bg-white/[0.07] text-foreground')}>
                  <button type="button" onClick={() => setFocus(focused === id ? null : id)} className="inline-flex items-center gap-1.5 pr-1" data-testid={`qt-backdrop-chip-${id}`}>
                    {thumb ? <img src={thumb} alt="" className="h-7 w-5 rounded object-cover" /> : <ImageIcon className="h-4 w-4" aria-hidden />}
                    <span className="max-w-[7rem] truncate">{name(id)}</span>
                    <span className="tabular-nums opacity-70">{see[id] ?? 100}%</span>
                  </button>
                  <button type="button" onClick={() => toggle(id)} aria-label={t(`${base}.remove`, { name: name(id) })} className="flex h-7 w-7 items-center justify-center rounded-full text-foreground/70 hover:bg-white/10">
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </span>
              );
            })}
          </div>
          <p className="pt-1.5 text-[12px] leading-snug text-muted-foreground">{t(`${base}.layers`, { max: MAX_BACKDROPS })}</p>
        </div>
      )}
      {open && (
        <LibraryPicker
          items={items}
          groups={groups}
          picked={chosen}
          max={MAX_BACKDROPS}
          onToggle={toggle}
          onNone={() => { set([]); setFocus(null); }}
          noneLabel={t(`${lib}.none`)}
          searchLabel={t(`${base}.search`)}
          noMatch={t(`${base}.noMatch`)}
          fullLabel={t(`${base}.full`, { max: MAX_BACKDROPS })}
          allLabel={t(`${lib}.all`)}
          clearLabel={t(`${lib}.clearSearch`)}
          offLabel={t(`${lib}.tapOff`)}
          base={{ photo }}
          backdrop
          fullBar={{ title: (n, max) => t(`${lib}.fullBar`, { n, max }), remove: (n) => t(`${base}.remove`, { name: n }) }}
          testid={`qt-library-${field.name}`}
        />
      )}
      {strength}
    </>
  );
  if (bare) return <div className="min-w-0" data-testid={`qt-backdrop-${field.name}`}>{body}</div>;
  return (
    <fieldset className="mt-5 min-w-0" data-testid={`qt-backdrop-${field.name}`}>
      <legend className="mb-1 text-[14px] font-medium text-muted-foreground">{t(`${base}.label`)}</legend>
      <p className="mb-2 text-[12.5px] text-muted-foreground">{t(`${base}.hint`)}</p>
      <input type="hidden" name={field.name} value={chosen.join(',')} />
      {body}
    </fieldset>
  );
}

/**
 * THE LIBRARY, ONE CHOICE FIRST (owner 2026-10-21: "before any category the first categories they go into are
 * backdrops and than overlays.... those need to be seperated... than oce thats established they go in their
 * respective categories"): a two-way switch, Backdrops | Overlays, each saying what is on; under the open one, its
 * own tray. Both picks are kept and sent whichever is open.
 */
function CoverLibrary({ tool, backdropField, floatField, backdrop, float, opacity, onBackdrop, onFloat, onOpacity, subject, depth, depthBusy, picked, cut, onCut, photo }: {
  tool: QuickToolKey; backdropField: FieldSpec; floatField: FieldSpec; backdrop: string; float: string; opacity: string;
  onBackdrop: (v: string) => void; onFloat: (v: string) => void; onOpacity: (v: string) => void;
  subject: string; depth: DepthPreview | null; depthBusy: boolean; picked: boolean; cut: boolean; onCut: (on: boolean | 'no') => void; photo: string | null;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'backdrops' | 'overlays'>('backdrops');
  const base = `qt.tools.${tool}.fields.library`;
  const floats = float.split(',').filter((v) => v && v !== 'none');
  // A backdrop is sent only once the customer removed their background and the cut-out worked.
  const chosen = cut && depth?.depth ? backdropIds(backdrop).join(',') : '';
  const overlayCount = Object.keys(LIB_OVERLAYS).length + (floatField.options || []).filter((o) => !LIB_OVERLAYS[o] && LIB_DRAWN[o]).length;
  const tabs = [
    { id: 'backdrops' as const, Icon: ImageIcon, count: Object.keys(LIB_BACKDROPS).length, on: chosen ? chosen.split(',').length : 0 },
    { id: 'overlays' as const, Icon: Sparkles, count: overlayCount, on: floats.length },
  ];
  return (
    <fieldset className="mt-6 min-w-0" data-testid="qt-cover-library">
      <legend className="mb-2.5 text-[15px] font-semibold text-foreground">{t(`${base}.label`)}</legend>
      <input type="hidden" name={backdropField.name} value={chosen} />
      <input type="hidden" name={floatField.name} value={floats.join(',') || 'none'} />
      <input type="hidden" name="opacity" value={opacity} />
      <div className="mb-4 grid grid-cols-2 gap-1 rounded-2xl bg-black/35 p-1 ring-1 ring-white/[0.06]" role="tablist" aria-label={t(`${base}.label`)}>
        {tabs.map(({ id, Icon, count, on }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            data-testid={`qt-library-tab-${id}`}
            className={cn('relative flex min-h-[56px] flex-col items-center justify-center rounded-xl px-2 py-1.5 transition-colors',
              tab === id ? 'bg-white/[0.1] text-foreground shadow-[0_6px_18px_-8px_rgba(0,0,0,0.8)]' : 'text-muted-foreground hover:text-foreground')}
          >
            <span className="flex items-center gap-1.5 text-[15px] font-bold">
              <Icon className="h-4 w-4" aria-hidden />
              {t(`${base}.${id}`)}
              <span className="text-[11.5px] font-medium tabular-nums opacity-60">{count}</span>
            </span>
            <span className="mt-0.5 text-[11.5px] leading-tight opacity-80">{on ? t(`${base}.picked`, { count: on }) : t(`${base}.${id}Hint`)}</span>
            {on > 0 && <span aria-hidden className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary" />}
          </button>
        ))}
      </div>
      {tab === 'backdrops' ? (
        <BackdropField bare tool={tool} field={backdropField} value={backdrop} onChange={onBackdrop} subject={subject} depth={depth} depthBusy={depthBusy} picked={picked} cut={cut} onCut={onCut} photo={photo} />
      ) : (
        <MultiField bare tool={tool} field={floatField} value={float} onChange={onFloat} opacity={opacity} onOpacity={onOpacity} />
      )}
    </fieldset>
  );
}

/** The customer's own clip, playing (the Living Cover's preview when a video is picked). */
function PickedClip({ file }: { file: File }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <video src={url} muted loop playsInline autoPlay className="aspect-[9/16] w-full rounded-3xl border border-white/10 bg-black object-cover" />;
}

/** An admin's button on a finished Living Cover: show it as an example on the site. */
function UseAsExample({ jobId, onAdded }: { jobId: string; onAdded: () => void }) {
  const { t } = useTranslation();
  const [admin, setAdmin] = useState(false);
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');
  useEffect(() => {
    const token = api.getToken();
    if (token) void isExamplesAdmin(token).then(setAdmin);
  }, []);
  if (!admin) return null;
  const add = async () => {
    const token = api.getToken();
    if (!token) return;
    setState('busy');
    const ok = await addCoverExample(jobId, token);
    setState(ok ? 'done' : 'failed');
    if (ok) onAdded();
  };
  return (
    <button
      type="button"
      onClick={() => void add()}
      disabled={state === 'busy' || state === 'done'}
      data-testid="qt-use-as-example"
      className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border border-primary/50 text-[15px] font-semibold text-foreground disabled:opacity-70"
    >
      {state === 'busy' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {t(state === 'done' ? 'qt.examples.added' : state === 'failed' ? 'qt.examples.addFailed' : 'qt.examples.add')}
    </button>
  );
}

/** The paid living cover's share link: open it, copy it, or share it. */
/** "1 h 05 min", "12 min", "40 s". */
function spokenLength(seconds: number) {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}

// FAMILY TAPE RESCUE'S RESULT (owner 2026-11-01): the family page, what was found on the tape, and the page's QR
// code right here, so it can be saved or printed without opening anything else.
function FamilyLink({ path, scenes, removed }: { path: string; scenes: number; removed: number }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${path}`;
  const fid = path.split('/').pop() || '';
  const share = async () => {
    trackEvent('quicktool_family_shared', {});
    try {
      if (navigator.share) {
        await navigator.share({ url, title: t('qt.family.title') });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* closed the share sheet */
    }
  };
  return (
    <div data-testid="qt-family-link" className="mt-4 rounded-2xl border border-primary/40 bg-primary/[0.07] p-4">
      <p className="font-display text-[17px] font-bold">{t('qt.family.linkTitle')}</p>
      {scenes > 0 && <p className="mt-1 text-[14px] font-semibold text-foreground">{t('qt.family.found', { count: scenes, removed: spokenLength(removed) })}</p>}
      <p className="mt-1 text-[14px] text-muted-foreground">{t('qt.family.linkLead')}</p>
      <div className="mt-3 flex items-center gap-3">
        <img src={`/api/family/${fid}/qr.png`} alt={t('qt.family.cardLine')} width={112} height={112} className="h-28 w-28 shrink-0 rounded-xl bg-white p-1" />
        <p className="break-all rounded-lg bg-black/30 px-3 py-2 font-mono text-[12.5px] text-foreground/85">{url}</p>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <a href={path} target="_blank" rel="noreferrer" className="flex min-h-[48px] items-center justify-center rounded-xl bg-accent px-2 text-center text-[15px] font-bold text-accent-foreground">
          {t('qt.family.open')}
        </a>
        <button type="button" onClick={() => void share()} className="flex min-h-[48px] items-center justify-center rounded-xl border border-white/15 text-[15px] font-semibold">
          {copied ? t('qt.family.copied') : t('qt.family.share')}
        </button>
      </div>
    </div>
  );
}

function CoverLink({ path }: { path: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${path}`;
  const share = async () => {
    trackEvent('quicktool_cover_shared', {});
    try {
      if (navigator.share) {
        await navigator.share({ url, title: t('qt.coverShareTitle') });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* closed the share sheet */
    }
  };
  return (
    <div data-testid="qt-cover-link" className="mt-4 rounded-2xl border border-primary/40 bg-primary/[0.07] p-4">
      <p className="font-display text-[17px] font-bold">{t('qt.coverTitle')}</p>
      <p className="mt-1 text-[14px] text-muted-foreground">{t('qt.coverLead')}</p>
      <p className="mt-2 break-all rounded-lg bg-black/30 px-3 py-2 font-mono text-[12.5px] text-foreground/85">{url}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <a href={path} target="_blank" rel="noreferrer" className="flex min-h-[48px] items-center justify-center rounded-xl border border-white/15 text-[15px] font-semibold">
          {t('qt.coverOpen')}
        </a>
        <button type="button" onClick={() => void share()} className="flex min-h-[48px] items-center justify-center rounded-xl bg-primary text-[15px] font-bold text-primary-foreground">
          {copied ? t('qt.coverCopied') : t('qt.coverShare')}
        </button>
      </div>
    </div>
  );
}

function FieldInput({
  tool,
  field,
  voices,
  voiceOff,
  short = false,
}: {
  tool: QuickToolKey;
  field: FieldSpec;
  voices?: { value: string; label: string }[];
  voiceOff?: boolean;
  /** The editor's short label (labels only, owner 2026-10-21). */
  short?: boolean;
}) {
  const { t } = useTranslation();
  const base = `qt.tools.${tool}.fields.${field.name}`;
  const label = voiceOff && field.name === 'script' ? t(`${base}.labelVoiceOff`) : short ? t(`${base}.short`, { defaultValue: t(`${base}.label`) }) : t(`${base}.label`);
  const input = 'w-full rounded-xl border border-white/15 bg-[hsl(220_18%_7%)] px-3.5 py-3 text-[16px] text-foreground outline-none transition-[border-color,box-shadow] focus:border-primary/70 focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]';
  if (field.type === 'checkbox') {
    return (
      <label className="mt-4 flex items-center gap-3 text-[15px] text-foreground">
        <input type="checkbox" name={field.name} defaultChecked={field.checked} className="h-5 w-5 accent-[hsl(var(--primary))]" />
        {label}
      </label>
    );
  }
  const options =
    field.name === 'voice' ? (voices || []).map((v) => ({ value: v.value, label: v.label })) : (field.options || []).map((o) => ({ value: o, label: t(`${base}.options.${o}`) }));
  return (
    <label className="mt-4 block first:mt-0">
      <span className="mb-1.5 block text-[14px] font-medium text-muted-foreground">{label}</span>
      {field.type === 'textarea' ? (
        <textarea name={field.name} rows={field.rows || 6} maxLength={field.max} required={field.required} placeholder={t(`${base}.placeholder`, { defaultValue: '' })} className={input} />
      ) : field.type === 'select' ? (
        <select name={field.name} className={input}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          name={field.name}
          type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
          inputMode={field.type === 'number' ? 'numeric' : field.type === 'url' ? 'url' : undefined}
          min={field.type === 'number' ? 0 : undefined}
          maxLength={field.max}
          required={field.required}
          placeholder={t(`${base}.placeholder`, { defaultValue: '' })}
          className={input}
        />
      )}
    </label>
  );
}

/**
 * WHERE TO PUT IT (owner 2026-10-19: "we should also provide the link for them to upload it right to their spotify or
 * their social media or whatsapp etc."): the upload pages for Spotify Canvas, TikTok and YouTube Shorts (a site cannot
 * hand a file to another site, so the buyer downloads first), WhatsApp with the cover's own link, and on a phone the
 * phone's own share sheet with the video itself (Instagram, WhatsApp Status, anything installed).
 */
function PostItLinks({ coverUrl, fileUrl, songUrl }: { coverUrl?: string | null; fileUrl: string; songUrl: string | null }) {
  const { t } = useTranslation();
  const [canShareFiles, setCanShareFiles] = useState(false);
  const [sharing, setSharing] = useState(false);
  useEffect(() => {
    try {
      const probe = new File([new Blob([''], { type: 'video/mp4' })], 'x.mp4', { type: 'video/mp4' });
      setCanShareFiles(typeof navigator !== 'undefined' && typeof navigator.canShare === 'function' && navigator.canShare({ files: [probe] }));
    } catch { setCanShareFiles(false); }
  }, []);
  const link = coverUrl ? new URL(coverUrl, window.location.origin).toString() : null;
  const shareVideo = async () => {
    setSharing(true);
    try {
      const r = await fetch(songUrl || fileUrl);
      const blob = await r.blob();
      const file = new File([blob], 'living-cover.mp4', { type: 'video/mp4' });
      await navigator.share({ files: [file], ...(link ? { text: link } : {}) });
      trackEvent('living_cover_shared', { how: 'native' });
    } catch { /* the buyer closed the share sheet */ } finally { setSharing(false); }
  };
  const pill = 'flex min-h-[46px] items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] px-3 text-[14px] font-semibold text-foreground hover:border-white/25';
  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="qt-post-it">
      <p className="text-[14px] font-semibold text-foreground">{t('qt.tools.canvas.postTitle')}</p>
      <p className="mt-0.5 text-[12.5px] text-muted-foreground">{t('qt.tools.canvas.postNote')}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <a href="https://artists.spotify.com/" target="_blank" rel="noopener noreferrer" className={pill} onClick={() => trackEvent('living_cover_post', { to: 'spotify' })}>
          <span className="text-[#1DB954]" translate="no">Spotify</span> <span translate="no">Canvas</span>
        </a>
        <a href="https://www.tiktok.com/upload" target="_blank" rel="noopener noreferrer" className={pill} onClick={() => trackEvent('living_cover_post', { to: 'tiktok' })} translate="no">TikTok</a>
        <a href="https://www.youtube.com/upload" target="_blank" rel="noopener noreferrer" className={pill} onClick={() => trackEvent('living_cover_post', { to: 'youtube' })} translate="no"><span className="text-[#FF0033]">YouTube</span> Shorts</a>
        {link && (
          <a href={`https://wa.me/?text=${encodeURIComponent(link)}`} target="_blank" rel="noopener noreferrer" className={pill} onClick={() => trackEvent('living_cover_post', { to: 'whatsapp' })} translate="no">WhatsApp</a>
        )}
      </div>
      {canShareFiles && (
        <button type="button" onClick={() => void shareVideo()} disabled={sharing} className="mt-2 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary/15 text-[15px] font-bold text-foreground ring-1 ring-primary/40 disabled:opacity-60" data-testid="qt-share-native">
          {sharing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Share2 className="h-4 w-4" aria-hidden />}
          {t('qt.tools.canvas.shareNative')}
        </button>
      )}
    </div>
  );
}

/** A slider for a 0..100 setting: the number the file is made with, and a word for where it sits. */
function RangeField({ tool, field, value, onChange }: { tool: QuickToolKey; field: FieldSpec; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const base = `qt.tools.${tool}.fields.${field.name}`;
  const n = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  const word = n < 34 ? 'low' : n < 67 ? 'mid' : 'high';
  // A drawn track and knob over the real slider (same in every browser), the filled part showing how much.
  return (
    <div className="mt-3 first:mt-0" data-testid={`qt-range-${field.name}`}>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={`qt-${field.name}`} className="text-[14px] font-semibold text-foreground">{t(`${base}.label`)}</label>
        <span className="rounded-full bg-white/[0.07] px-2.5 py-0.5 text-[12.5px] font-semibold tabular-nums text-foreground">{t(`${base}.steps.${word}`)} · {n}</span>
      </div>
      <div className="relative mt-1 h-10">
        <div aria-hidden className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-primary to-accent" style={{ width: `${n}%` }} />
        </div>
        <input
          id={`qt-${field.name}`}
          type="range"
          min={0}
          max={100}
          step={1}
          value={n}
          onChange={(e) => onChange(e.target.value)}
          className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-valuetext={`${t(`${base}.steps.${word}`)}, ${n}`}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_2px_10px_rgba(0,0,0,0.5)] ring-4 ring-primary/25 transition-shadow peer-focus-visible:ring-primary/70 peer-active:ring-8"
          style={{ left: `calc(12px + (100% - 24px) * ${n / 100})` }}
        />
      </div>
    </div>
  );
}
